import {
  CountType,
  ControlType,
  PreselectType,
  AvailabilityType,
  LockTypes,
  LockCountType,
  WeekendHandling,
  PendingUpdateType,
  AuditActionTypes,
  ItemTypes,
  PenaltyType,
  EventType,
  EventDay,
  Strictness,
  TaskType,
  PenaltyPointConversionType,
  ProfileRole,
  RewardType,
  ReportStyle,
  AvailabilityDayType,
  OwnerType,
  HygieneBreak,
  LovensePresetName,
  ProofStatus,
  TraceLevel,
} from "./enums";
import {
  isTimedItem,
  convertToCountTypeString,
  getCountMinutes,
  memberOwnerKeySuffix,
  isOutfitItem,
  isAvailableItem,
  cloneObject,
} from "../utilities/utils";
import { v1 as UUID, v1 } from "uuid";
import { MessageType, NotificationType } from "../controllers/messaging";
import StatefulSettings from "../controllers/StatefulSettings";
import {
  addDays,
  addMinutes,
  differenceInDays,
  getTime,
  isBefore,
  parseISO,
  startOfToday,
} from "date-fns";
import round from "lodash/round";
import { getProfileImage } from "../utilities/imageUtils";
import Cookies from "../utilities/cookies";
import {
  convertToMinutes,
  formatValidDate,
} from "../utilities/dateUtils";

export interface IKeyedItem {
  key: string;
}

export interface IReward {
  name: string;
  cost: number;
  type: RewardType;
}

export interface INamedItem {
  name: string;
}

export interface IPictureItem {
  img?: string;
  imgId: string | undefined;
}

export interface IVotableItem {
  votable?: boolean;
  voteCount?: number;
}

interface IMistakeItem {
  mistakeCount?: number;
  sendMistakesToPillory?: boolean;
  linesGameplayMode?: LinesGameplayMode;
}

export type LinesGameplayMode = "chill" | "balanced" | "brutal";

export interface IFavourite {
  isFavourite?: boolean;
}

export interface ISettingsItem
  extends INamedItem,
  ITaskSafety,
  IPictureItem,
  IKeyedItem {
  itemType: ItemTypes;
  description: string;
  hidden?: boolean;
  isDeleted?: boolean;
  isCustom?: boolean;
  isSystem?: boolean;
  isOwner?: boolean;
  isOverride?: boolean;
  isNew?: boolean;
  createdDate?: Date;
  createdBy?: string;
  requiresValidation?: boolean;
}

export abstract class BaseItem implements ISettingsItem {
  constructor(item: ISettingsItem) {
    this.img = item?.img;
    this.imgId = item?.imgId;
    this.key = item?.key;
    this.name = item?.name;
    this.description = item?.description;
    this.hidden = item?.hidden;
    this.isDeleted = item?.isDeleted;
    this.isSystem = item?.isSystem;
    this.isOwner = item?.isOwner;
    this.isCustom = item?.isCustom;
    this.isOverride = item?.isOverride;
    this.availabilityDayType = item?.availabilityDayType;
    this.isNew = item?.isNew;
    this.requiresValidation = item?.requiresValidation;
    this.createdBy = item?.createdBy;
    this.createdDate = item?.createdDate;
  }

  key: string;
  abstract itemType: ItemTypes;
  name: string;
  description: string;
  hidden?: boolean;
  isDeleted?: boolean;
  isSystem?: boolean;
  isOwner?: boolean;
  isCustom?: boolean;
  isOverride?: boolean;
  img?: string;
  imgId: string | undefined;
  availabilityDayType: AvailabilityDayType;
  isNew?: boolean;
  requiresValidation?: boolean;
  createdBy?: string;
  createdDate?: Date;
}

export interface IScheduledItem extends ISettingsItem, IOptionSupport {
  minTime?: string; // scheduling
  maxTime?: string; // scheduling
  eventType: EventType; // general
  availability: AvailabilityType; // scheduling
  disableAdd: boolean; // scheduling
  approvalChance: number; // settings
  specificDays?: EventDay[]; // scheduling
  categories?: string[]; // general
  requiredTasksInProgress?: string[]; // scheduling
  tasksEndTriggerEvent?: string; //scheduling
  proofTaskKeys?: string[]; // tasks
  missEventPenalties?: string[]; // scheduling
  missEventPillories?: string[]; // scheduling
  taskKeys?: string[]; // tasks
  selectedTaskKeys?: string[]; // persisted task picks for single-pick rules
  taskCategories?: string[]; // tasks
  penalties?: string[]; // tasks
  pillories?: string[]; // tasks
  pickSingleTask?: boolean; // tasks
  pickSinglePenalty?: boolean; // tasks
  pickSinglePillory?: boolean; // tasks
  pickSingleCategoryTask?: boolean; // tasks
  hasOptions: boolean; // options
  optionAppliedChance: number; // options
  webLinkOptions?: boolean;
}

export interface IPermissionItem
  extends IScheduledItem,
  ICategoryItem,
  ICountSupport {
  requiredCredit?: number;
  count?: number; // settings
  countType: CountType; // settings
  controlTypes?: ControlType[]; // settings
  isLimited: boolean; // settings
  alwaysApplyLimit?: boolean; // settings
}

// like a permission but can be auto scheduled
export interface IEventItem extends IScheduledItem, ICategoryItem {
  eventType: EventType;
}

// something which can be asked for and controlled when granted
export class PermissionItem
  extends BaseItem
  implements IPermissionItem, ICountSupport, IOptionSupport {
  constructor(input: IPermissionItem) {
    const permission = cloneObject(input) as IPermissionItem;
    super(permission);
    this.count = permission.count;
    this.countType = permission.countType;
    this.controlTypes = permission.controlTypes;
    this.options = permission.options;
    this.optionImages = permission.optionImages;
    this.minTime = permission.minTime;
    this.maxTime = permission.maxTime;
    this.requiredCredit = permission.requiredCredit;
    this.eventType = permission.eventType || EventType.standard;
    this.availability = permission.availability;
    this.approvalChance = isNaN(permission.approvalChance)
      ? 1
      : permission.approvalChance ?? 0;
    this.disableAdd = permission.disableAdd;
    this.requiredCredit = permission.requiredCredit || 0;
    this.alwaysApplyLimit = permission.alwaysApplyLimit;
    this.optionAppliedChance = permission.optionAppliedChance;
    this.taskKeys = permission.taskKeys;
    this.selectedTaskKeys = permission.selectedTaskKeys;
    this.taskCategories = permission.taskCategories;
    this.specificDays = permission.specificDays;
    this.tasksEndTriggerEvent = permission.tasksEndTriggerEvent;
    this.categories = permission.categories;
    this.penalties = permission.penalties;
    this.requiredTasksInProgress = permission.requiredTasksInProgress;
    this.webLinkOptions = permission.webLinkOptions;
    this.pickSinglePenalty = permission.pickSinglePenalty;
    this.pickSingleCategoryTask = permission.pickSingleCategoryTask;
    this.pickSingleTask = permission.pickSingleTask;
    this.proofTaskKeys = permission.proofTaskKeys;
    this.missEventPenalties = permission.missEventPenalties;
    this.missEventPillories = permission.missEventPillories;
    this.pillories = permission.pillories;
    this.pickSinglePillory = permission.pickSinglePillory;
    this.monitoredTimer = permission.monitoredTimer;
  }

  monitoredTimer?: boolean | undefined;
  get isLimited(): boolean {
    return this.count && this.count > 0 ? true : false;
  }

  get hasOptions(): boolean {
    return this.options && this.options.length > 0 ? true : false;
  }
  webLinkOptions?: boolean;
  itemType = ItemTypes.permission;
  disableAdd = false;
  count?: number;
  countType: CountType;
  controlTypes?: ControlType[];
  options?: string[];
  optionImages?: IPictureItem[];
  minTime?: string;
  maxTime?: string;
  requiredCredit?: number;
  availability: AvailabilityType;
  approvalChance: number;
  alwaysApplyLimit?: boolean;
  optionAppliedChance: number;
  eventType: EventType;
  taskKeys?: string[];
  selectedTaskKeys?: string[];
  taskCategories?: string[];
  specificDays?: EventDay[];
  tasksEndTriggerEvent?: string;
  categories?: string[];
  penalties?: string[];
  requiredTasksInProgress?: string[];
  pickSingleTask?: boolean;
  pickSinglePenalty?: boolean;
  pickSingleCategoryTask?: boolean;
  proofTaskKeys?: string[];
  missEventPenalties?: string[];
  missEventPillories?: string[];
  pillories?: string[];
  pickSinglePillory?: boolean;
}

export class EventItem extends BaseItem implements IEventItem {
  constructor(input: IEventItem) {
    const event = cloneObject(input) as IEventItem;
    super(event);
    this.minTime = event.minTime;
    this.maxTime = event.maxTime;
    this.eventType = event.eventType || EventType.standard;
    this.availability = event.availability;
    this.approvalChance = isNaN(event.approvalChance)
      ? 1
      : event.approvalChance ?? 0;
    this.options = event.options;
    this.optionImages = event.optionImages;
    this.optionAppliedChance = event.optionAppliedChance;
    this.disableAdd = event.disableAdd;
    this.taskKeys = event.taskKeys;
    this.selectedTaskKeys = event.selectedTaskKeys;
    this.taskCategories = event.taskCategories;
    this.specificDays = event.specificDays;
    this.tasksEndTriggerEvent = event.tasksEndTriggerEvent;
    this.categories = event.categories;
    this.requiredTasksInProgress = event.requiredTasksInProgress;
    this.penalties = event.penalties;
    this.pickSinglePenalty = event.pickSinglePenalty;
    this.pickSingleCategoryTask = event.pickSingleCategoryTask;
    this.pickSingleTask = event.pickSingleTask;
    this.proofTaskKeys = event.proofTaskKeys;
    this.missEventPenalties = event.missEventPenalties;
    this.missEventPillories = event.missEventPillories;
    this.webLinkOptions = event.webLinkOptions;
    this.pillories = event.pillories;
    this.pickSinglePillory = event.pickSinglePillory;
  }

  get hasOptions(): boolean {
    return this.options && this.options.length > 0 ? true : false;
  }

  webLinkOptions?: boolean;
  itemType = ItemTypes.event;
  disableAdd = false;
  minTime?: string;
  maxTime?: string;
  availability: AvailabilityType;
  options?: string[];
  optionImages?: IPictureItem[];
  optionAppliedChance: number;
  approvalChance: number;
  eventType: EventType;
  taskKeys?: string[];
  selectedTaskKeys?: string[];
  taskCategories?: string[];
  specificDays?: EventDay[];
  tasksEndTriggerEvent?: string;
  categories?: string[];
  requiredTasksInProgress?: string[];
  penalties?: string[];
  pickSingleTask?: boolean;
  pickSinglePenalty?: boolean;
  pickSingleCategoryTask?: boolean;
  proofTaskKeys?: string[];
  missEventPenalties?: string[];
  missEventPillories?: string[];
  pillories?: string[];
  pickSinglePillory?: boolean;
}

/**
 * Indicates that a member needs an owner for a task to be selected
 */
export interface IOwnerRequired {
  requiresOwner?: boolean;
}

export interface ICountSupport {
  count?: number;
  countType: CountType;
  expectedCount?: number;
  /// when running should this item allow other actions until complete
  monitoredTimer?: boolean;
}
export interface IOptionSupport {
  options?: string[];
  webLinkOptions?: boolean;
  useRouletteForOptions?: boolean;
  selectedCondition?: string;
  optionImages?: IPictureItem[];
}

export interface IItemSupport extends IKeyedItem {
  itemsUsed?: string[][]; // does it have a set of items which should be used
  isItem?: boolean; // is it itself an item to use
  selectedItems?: Record<string, string>; // [itemName] [- condition if any]
  selectedChoiceName?: string;
  selectedItemKeys?: string[];
}

export interface ITaskSafety {
  availabilityDayType: AvailabilityDayType;
}

interface IPreselectSupport {
  preSelect: PreselectType;
}

export interface ICategoryItem {
  /// categories which cannot exist alongside this
  categories?: string[];
}

interface IEndTriggerEventSupport {
  endTriggerEvent?: string;
  rollOverEventDays?: boolean;
}

export interface ILovenseToy extends IVotableItem {
  lovenseToyId?: string;
  lovenseProfile?: LovensePresetName;
  isLovenseToy?: boolean;
}

interface IMultipleRunsSupport {
  timesRequired?: number;
  taskVotes?: ITaskVoteConfig[];
}

export interface ITaskItem
  extends ISettingsItem,
  IOptionSupport,
  IOwnerRequired,
  IItemSupport,
  ICountSupport,
  IMultipleRunsSupport,
  IPreselectSupport,
  ICategoryItem,
  IFavourite,
  IToyTabItem,
  IVotableItem,
  IMistakeItem,
  IEndTriggerEventSupport,
  ILovenseToy {
  credit: number;
  count?: number;
  countType: CountType;
  categories?: string[];
  selectCategoryItem?: string; // allows a task to pick randomly from a set of tasks when selected and returns the newly selected item instead of the item selected from the shop
  creditTaskType?: TaskType;
  alwaysShowTime?: boolean; // ignore the hide timer setting for items which need to see time to work properly
  suppressScheduleTask?: boolean; // if this task should always appear in task list immediately
  taskKeys?: string[]; // allows a task to fire other tasks
  penaltyKeys?: string[]; // allows a task to fire other penalties
  pilloryKeys?: string[]; // allows a task to fire send to pillory
  captureLockImage?: boolean; // should we allow lock images to be capture for these tasks
  proofStatus?: ProofStatus;
  itemOptions?: Record<number, Record<string, string>>;
  choiceNames?: Record<number, string>;
  weeklyTargetValue?: number;
  weeklyTargetUnit?: string;
  weeklyTargetPenaltyKey?: string;
}

export type WeeklyTargetReviewDecision = "approved" | "applyPenalty" | "ended";

export interface IWeeklyTargetProgress {
  taskKey: string;
  weekKey: string;
  startedAt: string;
  dueAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  targetValue: number;
  achievedValue?: number;
  proofCode?: string;
  proofImageUrl?: string;
  proofImageId?: string;
  decision?: WeeklyTargetReviewDecision;
  decisionReason?: string;
  reviewerUserid?: string;
  reviewerUsername?: string;
  penaltyApplied?: boolean;
  penaltyKey?: string;
  nextWeekTargetValue?: number;
}

export interface IWeeklyTargetProofMessageState {
  userid: string;
  username?: string;
  taskKey: string;
  taskName: string;
  weekKey: string;
  targetValue: number;
  achievedValue?: number;
  proofImageUrl?: string;
  proofImageId?: string;
  penaltyKey?: string;
  penaltyApplied?: boolean;
  history?: IWeeklyTargetProgress[];
}

export interface ITaskVoteConfig {
  taskKey?: string;
  voteIncrement: number;
  isUp: boolean;
  countType: CountType;
}

export class TaskItem extends BaseItem implements ITaskItem {
  constructor(input: ITaskItem) {
    super(input);
    const task = cloneObject(input) as ITaskItem;
    this.votable = task.votable;
    this.mistakeCount = task.mistakeCount;
    this.sendMistakesToPillory = task.sendMistakesToPillory;
    this.linesGameplayMode = task.linesGameplayMode;
    this.options = task.options;
    this.optionImages = task.optionImages;
    this.count = task.count;
    this.countType = task.countType;
    this.credit = task.credit;
    this.preSelect = task.preSelect;
    this.useRouletteForOptions = task.useRouletteForOptions;
    this.webLinkOptions = task.webLinkOptions;
    this.isItem = task.isItem || false;
    this.itemsUsed = Array.isArray(task.itemsUsed)
      ? task.itemsUsed
        .filter((set): set is string[] => Array.isArray(set))
        .map((set) => set.filter((value): value is string => typeof value === "string" && value.length > 0))
        .filter((set) => set.length > 0)
      : [];

    if (this.itemsUsed)
      this.itemsUsed.forEach((set) => {
        const idx = set.indexOf(input.key);
        if (idx > -1) set.splice(idx, 1);
      });

    this.itemsUsed = this.itemsUsed.filter((set) => set.length > 0);
    this.categories = Array.isArray(task.categories)
      ? task.categories.filter((value): value is string => typeof value === "string" && value.length > 0)
      : task.categories;
    this.endTriggerEvent = task.endTriggerEvent;
    this.selectCategoryItem = task.selectCategoryItem;
    this.creditTaskType = task.creditTaskType ?? TaskType.standard;
    this.itemType = task.itemType ?? ItemTypes.task;
    this.alwaysShowTime = task.alwaysShowTime;
    this.suppressScheduleTask = task.suppressScheduleTask;
    this.taskKeys = task.taskKeys;
    this.penaltyKeys = task.penaltyKeys;
    this.pilloryKeys = task.pilloryKeys;
    this.taskVotes = task.taskVotes;
    this.captureLockImage = task.captureLockImage;
    this.lovenseToyId = task.lovenseToyId;
    this.lovenseProfile = task.lovenseProfile;
    this.voteCount = task.voteCount;
    this.isLovenseToy = task.isLovenseToy;
    this.requiresOwner = task.requiresOwner;
    this.rollOverEventDays = task.rollOverEventDays;
    this.itemOptions = task.itemOptions;
    this.choiceNames = task.choiceNames;
    this.weeklyTargetValue = task.weeklyTargetValue;
    this.weeklyTargetUnit = task.weeklyTargetUnit;
    this.weeklyTargetPenaltyKey = task.weeklyTargetPenaltyKey;
    this.proofStatus = task.proofStatus || ProofStatus.random;
    this.timesRequired = task.timesRequired;
    this.monitoredTimer = task.monitoredTimer;
  }

  get expireOnPermission(): boolean {
    return this.endTriggerEvent !== undefined;
  }

  get isLimited(): boolean {
    return this.count && this.count > 0 ? true : false;
  }

  get hasOptions(): boolean {
    return this.options && this.options.length > 0 ? true : false;
  }

  monitoredTimer?: boolean | undefined;
  taskKeys?: string[];
  taskVotes?: ITaskVoteConfig[];
  penaltyKeys?: string[];
  pilloryKeys?: string[];
  itemType: ItemTypes;
  options?: string[];
  optionImages?: IPictureItem[];
  isItem?: boolean;
  itemsUsed?: string[][];
  credit: number;
  count?: number;
  countType: CountType;
  preSelect: PreselectType;
  useRouletteForOptions?: boolean;
  webLinkOptions?: boolean;
  categories?: string[];
  endTriggerEvent?: string;
  selectCategoryItem?: string;
  creditTaskType?: TaskType;
  votable?: boolean | undefined;
  mistakeCount?: number | undefined;
  sendMistakesToPillory?: boolean;
  linesGameplayMode?: LinesGameplayMode;
  alwaysShowTime?: boolean; // ignore the hide timer setting for items which need to see time to work properly
  suppressScheduleTask?: boolean; // if this task should always appear in task list immediately
  captureLockImage?: boolean;
  isLovenseToy?: boolean;
  lovenseToyId?: string;
  lovenseProfile?: LovensePresetName;
  voteCount?: number;
  requiresOwner?: boolean;
  proofStatus?: ProofStatus;
  rollOverEventDays?: boolean;
  itemOptions?: Record<number, Record<string, string>>;
  choiceNames?: Record<number, string>;
  weeklyTargetValue?: number;
  weeklyTargetUnit?: string;
  weeklyTargetPenaltyKey?: string;
  timesRequired?: number;


  public static IsAnItemToOwn(item: ITaskItem): boolean {
    return TaskItem.IsItemToOwn(item) || (item.itemsUsed && item.itemsUsed.length > 0) || false;
  }

  /**
   * Can this type of task be timed or is it a play and done type of task
   * @param task
   * @returns
   */
  public static IsStandardTask(task: ITaskItem): boolean {
    return (
      task.creditTaskType === TaskType.standard ||
      task.creditTaskType === undefined
    );
  }

  public static IsWeeklyNumericTargetTask(task: ITaskItem): boolean {
    return task.creditTaskType === TaskType.weeklyNumericTarget;
  }

  /**
   * is this a lovense task that fires and runs for a timer
   * @param task
   * @returns
   */
  public static IsLovenseTimed(
    item: ILovenseToy & IVotableItem & ICountSupport
  ): boolean {
    return !item.votable && this.IsTimed(item) && item.isLovenseToy === true;
  }

  /**
   * is this a lovense task that runs as a vote and activates on each vote
   * @param task
   * @returns
   */
  public static IsLovenseVote(
    item: ILovenseToy & IVotableItem & ICountSupport
  ): boolean {
    return (
      item.votable === true &&
      item.voteCount !== undefined &&
      item.voteCount > 0 &&
      item.isLovenseToy === true
    );
  }

  /**
   * Can this type of task be timed or is it a play and done type of task
   * @param task
   * @returns
   */
  public static ItemTypeCanBeTimed(task: ITaskItem): boolean {
    return (
      TaskItem.IsStandardTask(task) ||
      task.creditTaskType === TaskType.categoryPicker ||
      task.creditTaskType === TaskType.chastity ||
      task.creditTaskType === TaskType.weeklyNumericTarget
    );
  }

  /**
   * Is this task timed
   * @param task
   * @returns
   */
  public static IsTimed(task: ICountSupport): boolean {
    return (
      task.count !== undefined && task.count > 0 && isTimedItem(task.countType)
    );
  }

  /**
   * Is this task scheduled to take place in the week or is it just a task to pick from the pool
   * @param task
   * @returns
   */
  public static IsScheduled(task: ITaskItem): boolean {
    return task.preSelect !== 0 && task.preSelect !== undefined;
  }

  /**
   * Can this task support the concept of turning on and off the scheduler picking the task
   * during the day
   * @param task
   * @returns are all other settings correct for this setting to make sense
   */
  public static CanSuppressTaskItemSchedule(task: ITaskItem): boolean {
    return (
      !TaskItem.IsPermanentlyApplied(task) && !TaskItem.HasEndTriggerEvent(task)
    ); // logic here is that as it has an event it has to be early in case event is early
  }

  /**
   * is the task ended by a trigger event instead of a timer
   * @param task
   * @returns
   */
  public static HasEndTriggerEvent(task: ITaskItem): boolean {
    return (
      task.endTriggerEvent !== undefined && !TaskItem.IsSpecialTaskType(task)
    );
  }

  /**
   * Can this task be scheduled during the day or must it only be at Start Of Day
   * @param task
   * @returns can this task be scheduled during the day
   */
  public static CanTaskBeScheduledInDay(task: ITaskItem): boolean {
    return (
      task.suppressScheduleTask !== true &&
      TaskItem.CanSuppressTaskItemSchedule(task)
    );
  }

  /**
   * Can this task be selected in the middle of the day
   * items with schedules suppressed cannot be done intra day
   * @param task
   * @returns can this task be selected in the day
   */
  public static CanTaskBeSelectedIntraDay(task: ITaskItem): boolean {
    return (
      isAvailableItem(task) &&
      TaskItem.CanTaskBeScheduledInDay(task)
    );
  }

  /**
   * Can this task be selected in the middle of the day
   * items with schedules suppressed cannot be done intra day
   * @param task
   * @returns can this task be selected in the day
   */
  public static CanTaskBeSelectedStartOfDay(task: ITaskItem): boolean {
    return isAvailableItem(task);
  }

  /**
   * Is this task permanently applied and therefore no available for selection daily
   * @param task
   * @returns
   */
  public static IsPermanentlyApplied(task: ITaskItem): boolean {
    return task.preSelect === PreselectType.permanent;
  }

  /**
   * Can this task be selected selected by the system
   * @param task
   * @returns can this task be selected by the system for a schedule
   */
  public static CanSystemSelectTask(task: ITaskItem): boolean {
    return (
      !TaskItem.IsPermanentlyApplied(task) &&
      isAvailableItem(task)
    );
  }

  /**
   * Can this task be voted upon when it is underway
   * @param task
   * @returns is it a numerical count and is it not permanent and not ending on a trigger
   */
  public static IsVotable(task: ITaskItem | IPenaltyItem): boolean {
    return (
      isTimedItem(task.countType) &&
      (task instanceof PenaltyItem ||
        (task instanceof TaskItem &&
          !TaskItem.IsPermanentlyApplied(task) &&
          !TaskItem.HasEndTriggerEvent(task) &&
          !TaskItem.IsSpecialTaskType(task)))
    );
  }
  /**
   * Can this task be set to expire on an event
   */
  public static CanExpireOnEvent(task: ITaskItem): boolean {
    return (
      !TaskItem.IsPermanentlyApplied(task) && !TaskItem.IsSpecialTaskType(task)
    );
  }
  /**
   * Is this a task which is special and does not follow the standard rules
   */
  public static IsSpecialTaskType(task: ITaskItem): boolean {
    return (
      TaskItem.IsTaskPickerTask(task) ||
      TaskItem.IsRouletteTask(task) ||
      TaskItem.IsLinesTask(task) ||
      TaskItem.IsWeeklyNumericTargetTask(task)
    );
  }

  /**
   * is this a simple instruction and nothing more
   * @param task 
   * @returns 
   */
  public static IsSimpleRule(task: ITaskItem | IPenaltyItem): boolean {
    return task.endTriggerEvent === undefined
      && ((task.itemType === ItemTypes.penalty) || TaskItem.IsStandardTask(task as ITaskItem))
      && !task.count
  }

  /**
 * is this a request for proof for something else and nothing more
 * @param task 
 * @returns 
 */
  public static IsProofRequestTask(task: ITaskItem | IPenaltyItem): boolean {
    return TaskItem.IsSimpleRule(task)
      && task.proofStatus === ProofStatus.alwaysRequestProof
      && !task.isItem
      && (task.itemsUsed === undefined || task.itemsUsed.length === 0);
  }


  /**
   * is this task a roulette task that allocates another at random
   */
  public static IsRouletteTask(task: ITaskItem): boolean {
    return task.creditTaskType === TaskType.roulette;
  }
  /**
   * is this task a roulette task that allocates another at random
   */
  public static IsValidRouletteTask(task: ITaskItem): boolean {
    return (
      task.creditTaskType === TaskType.roulette &&
      ((task.taskKeys !== undefined && task.taskKeys.length > 0) ||
        (task.taskVotes !== undefined && task.taskVotes.length > 0) ||
        (task.penaltyKeys !== undefined && task.penaltyKeys.length > 0) ||
        (task.pilloryKeys !== undefined && task.pilloryKeys.length > 0) ||
        (task.options !== undefined && task.options.length > 0))
    );
  }
  /**
   * is this task a roulette task that allocates another at random
   */
  public static HasMoreThanOneChoiceValidOnRoulette(task: ITaskItem): boolean {
    if (!TaskItem.IsValidRouletteTask(task)) return false;
    return (task.taskKeys ? task.taskKeys.length : 0)
      + (task.taskVotes ? task.taskVotes.length : 0)
      + (task.penaltyKeys ? task.penaltyKeys.length : 0)
      + (task.pilloryKeys ? task.pilloryKeys.length : 0)
      + (task.options ? task.options.length : 0) > 1;
  }

  public static GetOnlyRouletteOption(task: ITaskItem) {
    if (TaskItem.HasMoreThanOneChoiceValidOnRoulette(task)) return undefined;
    if (task.taskKeys && task.taskKeys.length > 0) return task.taskKeys[0];
    if (task.taskVotes && task.taskVotes.length > 0) return task.taskVotes[0];
    if (task.penaltyKeys && task.penaltyKeys.length > 0) return task.penaltyKeys[0];
    if (task.pilloryKeys && task.pilloryKeys.length > 0) return task.pilloryKeys[0];
    if (task.options && task.options.length > 0) return task.options[0];
    return undefined;
  }

  /**
   * is this task a roulette task that allocates another at random
   */
  public static IsChastityTask(task: ITaskItem): boolean {
    return task.creditTaskType === TaskType.chastity;
  }

  /**
   * is this task a lines task that allocates another at random
   */
  public static IsLinesTask(task: ITaskItem): boolean {
    return task.creditTaskType === TaskType.lines;
  }

  /**
   * Is this task a category task which picks another task from the category specified
   */
  public static IsTaskPickerTask(task: ITaskItem): boolean {
    return task.creditTaskType === TaskType.categoryPicker;
  }

  /**
   * Is this an item to own
   */
  public static IsItemToOwn(task: IToyTabItem): boolean {
    return task.isItem === true && !this.IsSpecialTaskType(task as ITaskItem);
  }
}

export interface IToyTabItem extends ILovenseToy, ICountSupport, IItemSupport {
  incompleteGroups?: boolean;
  itemsUsedInUpdate?: boolean;
}

export interface IPenaltyItem
  extends ISettingsItem,
  IOptionSupport,
  IItemSupport,
  ICountSupport,
  IMultipleRunsSupport,
  ICategoryItem,
  IFavourite,
  IVotableItem,
  IMistakeItem,
  IEndTriggerEventSupport,
  ILovenseToy,
  IOwnerRequired {
  points: number;
  count?: number;
  countType: CountType;
  options: string[];
  penaltyType: PenaltyType;
  categories?: string[];
  taskKeys?: string[];
  penaltyKeys?: string[];
  pilloryKeys?: string[];
  alwaysShowTime?: boolean; // ignore the hide timer setting for items which need to see time to work properly
  captureLockImage?: boolean; // should we allow lock images to be capture for these tasks
  requiresOwner?: boolean;
  proofStatus?: ProofStatus;
}

export class PenaltyItem extends BaseItem implements IPenaltyItem {
  constructor(input: IPenaltyItem) {
    const penaltyItem = cloneObject(input) as IPenaltyItem;
    super(penaltyItem);
    this.mistakeCount = penaltyItem.mistakeCount;
    this.sendMistakesToPillory = penaltyItem.sendMistakesToPillory;
    this.linesGameplayMode = penaltyItem.linesGameplayMode;
    this.votable = penaltyItem.votable;
    this.points = penaltyItem.points;
    this.count = penaltyItem.count;
    this.countType = penaltyItem.countType ?? CountType.none;
    this.options = penaltyItem.options;
    this.optionImages = penaltyItem.optionImages;
    this.penaltyType = penaltyItem.penaltyType;
    this.endTriggerEvent = penaltyItem.endTriggerEvent;
    this.isItem = false; // penalties just use items
    this.itemsUsed = penaltyItem.itemsUsed || [];
    this.categories = penaltyItem.categories;
    this.taskKeys = penaltyItem.taskKeys;
    this.penaltyKeys = penaltyItem.penaltyKeys;
    this.webLinkOptions = penaltyItem.webLinkOptions;
    this.useRouletteForOptions = penaltyItem.useRouletteForOptions;
    this.alwaysShowTime = penaltyItem.alwaysShowTime;
    this.captureLockImage = penaltyItem.captureLockImage;
    this.lovenseToyId = penaltyItem.lovenseToyId;
    this.lovenseProfile = penaltyItem.lovenseProfile;
    this.voteCount = penaltyItem.voteCount;
    this.isLovenseToy = penaltyItem.isLovenseToy;
    this.requiresOwner = penaltyItem.requiresOwner;
    this.rollOverEventDays = penaltyItem.rollOverEventDays;
    this.proofStatus = penaltyItem.proofStatus ?? ProofStatus.random;
    this.timesRequired = penaltyItem.timesRequired;
    this.monitoredTimer = penaltyItem.monitoredTimer;
    this.taskVotes = penaltyItem.taskVotes;
  }
  get expireOnPermission(): boolean {
    return this.endTriggerEvent !== undefined;
  }

  get isLimited(): boolean {
    return this.count && this.count > 0 ? true : false;
  }

  get hasOptions(): boolean {
    return this.options && this.options.length > 0 ? true : false;
  }

  monitoredTimer?: boolean;
  useRouletteForOptions?: boolean;
  webLinkOptions?: boolean;
  taskKeys?: string[];
  penaltyKeys?: string[];
  isItem: false;
  itemsUsed?: string[][];
  itemType = ItemTypes.penalty;
  points = 1;
  count?: number;
  countType: CountType;
  options: string[];
  optionImages?: IPictureItem[];
  penaltyType: PenaltyType;
  endTriggerEvent?: string;
  votable?: boolean | undefined;
  mistakeCount?: number | undefined;
  sendMistakesToPillory?: boolean;
  linesGameplayMode?: LinesGameplayMode;
  categories?: string[];
  captureLockImage?: boolean;
  alwaysShowTime?: boolean; // ignore the hide timer setting for items which need to see time to work properly
  lovenseToyId?: string;
  lovenseProfile?: LovensePresetName;
  voteCount?: number;
  isLovenseToy?: boolean;
  requiresOwner?: boolean;
  proofStatus?: ProofStatus;
  rollOverEventDays?: boolean;
  timesRequired?: number;
  taskVotes?: ITaskVoteConfig[];

  /**
   * Is this penalty a standard task to perform
   */
  public static IsPenaltyTask(penalty: IPenaltyItem): boolean {
    return penalty.penaltyType === PenaltyType.task || !penalty.penaltyType;
  }
  /**
   * is this task a lines task that allocates another at random
   */
  public static IsLinesPenalty(penalty: IPenaltyItem): boolean {
    return penalty.penaltyType === PenaltyType.lines;
  }
  /**
   * is the task ended by a trigger event instead of a timer
   * @param task
   * @returns
   */
  public static HasEndTriggerEvent(penalty: IPenaltyItem): boolean {
    return (
      penalty.endTriggerEvent !== undefined &&
      !PenaltyItem.IsSpecialTaskType(penalty)
    );
  }
  /**
   * Is this a task which is special and does not follow the standard rules
   */
  public static IsSpecialTaskType(task: IPenaltyItem): boolean {
    return task.penaltyType !== PenaltyType.task;
  }
}

export class PositionItem extends BaseItem implements ISettingsItem {
  itemType = ItemTypes.position;
  isPosition = true;
  countType = CountType.minutes;
  monitoredTimer = true;
}

export interface ITaskProofRequestItem extends TaskBasketItem {
  proofCode: string;
}
export interface IPenaltyProofRequestItem extends PenaltyBasketItem {
  proofCode: string;
}

export interface IProofRequest {
  open: boolean;
  items: (ITaskProofRequestItem | IPenaltyProofRequestItem)[];
}

export interface ILinesRequest {
  count: number;
  line: string;
  item: ITaskItem | IPenaltyItem;
}

export interface IRouletteRequest {
  open: boolean;
  handled?: boolean;
  options: string[] | undefined;
  title: string;
  description: string;
  rouletteTask: ITaskItem | IPenaltyItem;
  itemType: ItemTypes;
}

export interface IReportRequest {
  open: boolean;
  count: number;
}

export interface IHygieneOptions {
  timedLock?: boolean;
  unlockStartTime?: Date;
  unlockEndTime?: Date;
  penalties?: string[];
  pillories?: string[];
  hasCostPerMin?: boolean;
  costPerMin?: number;
}

export interface IRequestLockState extends IHygieneOptions {
  result: ILockReceipt | undefined;
  item: ITaskTimerInfo | IPenaltyTimerInfo;
  isPaused: boolean; // determines if we are forcing a user to reload up an image
  captureUpload: boolean;
}

export interface ISelectionItem extends ISettingsItem {
  overrideDisableReason?: string;
  isUnavailable?: boolean | undefined;
}

export interface ITaskSettings {
  creditCountVariance: number;
  creditVariance: number;
  dailyMandatoryCredits: number;
  disableMemberAdditions: boolean;
  credits: ITaskItem[];
  disableSystemTasks: boolean;
  singleItemCategories?: string[];
  scheduleDailyTasks?: boolean;
  maxScheduleTime?: string;
}

export interface IPenaltySettings {
  penalties: IPenaltyItem[];
}

export interface IPermissionSettings {
  disablePermissions: boolean;
  permissionDurationVariance: number;
  permissions: IPermissionItem[];
}

export interface IEventSettings {
  permissionDurationVariance: number;
  disableEvents?: boolean;
  events: IEventItem[];
}

export interface IPositionSettings {
  disablePositions: boolean;
  minPositionCount: number;
  maxPositionCount: number;
  positionUpperDuration: number;
  positionLowerDuration: number;
  positionCompleteCredit: number;
  positions: ISettingsItem[];
}

export interface ISiteEffectsImageSettings {
  enabled: boolean;
  clickable: boolean;
  corruption: boolean;
  urls: string[];
  perMin: number;
  imagesPerFlash: number;
  maxOnScreen: number;
  imageDurationSec: number;
}

export interface ISiteEffectsVideoSettings {
  enabled: boolean;
  strictLock: boolean;
  urls: string[];
  perHour: number;
  miniGameEnabled: boolean;
  miniGameTargets: number;
  miniGameDurationSec: number;
  miniGameTargetSizePx: number;
  miniGameButtonMessages: string[];
}

export interface ISiteEffectsAudioSettings {
  enabled: boolean;
  perHour: number;
  urls: string[];
}

export type SiteFxProvider = "tumblr" | "soundcloud";

export type SiteFxMediaType = "image" | "video" | "audio";

export interface ISiteFxCuratedSource {
  key: string;
  /**
   * Legacy field retained for backward compatibility.
   * Provider is now inferred from the source URL.
   */
  provider?: SiteFxProvider;
  mediaType: SiteFxMediaType;
  /**
   * Legacy source-level categories retained for backward compatibility.
   * Resolver category matching now uses feed/item tags instead.
   */
  categories?: string[];
  /**
   * Legacy single-category field retained for backward compatibility with older config payloads.
   */
  category?: string;
  enabled: boolean;
  /**
   * Public Tumblr blog hostname (without protocol) or SoundCloud user page URL.
   */
  source: string;
}

export interface ISiteFxCuratedSourcesConfig {
  sources: ISiteFxCuratedSource[];
}

export interface ISiteFxMediaResolveRequest {
  category?: string;
  mediaTypes?: SiteFxMediaType[];
  bypassCache?: boolean;
  includeDiagnostics?: boolean;
  curatedSources?: ISiteFxCuratedSource[];
}

export interface ISiteFxSourceDiagnostics {
  key: string;
  provider: SiteFxProvider;
  source: string;
  resolvedSource?: string;
  categories: string[];
  itemCounts: Record<SiteFxMediaType, number>;
  firstItems: Partial<Record<SiteFxMediaType, string>>;
  error?: string;
}

export interface ISiteFxResolveDiagnostics {
  functionVersion: string;
  requestedMediaTypes: SiteFxMediaType[];
  selectedSources: string[];
  sources: ISiteFxSourceDiagnostics[];
}

export interface ISiteFxMediaResolveResponse {
  categoryRequested: string;
  categoryResolved: string;
  cacheHit: boolean;
  sourcesChecked: number;
  availableCategories: string[];
  categoryMediaTypes?: Record<string, SiteFxMediaType[]>;
  items: Record<SiteFxMediaType, string[]>;
  diagnostics?: ISiteFxResolveDiagnostics;
}

export interface ISiteEffectsMessageSettings {
  enabled: boolean;
  messages: string[];
  perMin: number;
  frames: number;
  opacityPercent: number;
  messageDurationMinMs: number;
  messageDurationMaxMs: number;
}

export interface ISiteEffectsSettings {
  backcolour: string;
  backgroundModes: string[];
  siteFxCategories?: string[];
  images: ISiteEffectsImageSettings;
  videos: ISiteEffectsVideoSettings;
  audio: ISiteEffectsAudioSettings;
  messages: ISiteEffectsMessageSettings;
}

export const SITE_FX_BACKGROUND_MODES = [
  { id: "arms", label: "Rotating Arms" },
  { id: "rings", label: "Circular Rings" },
] as const;

export const defaultSiteEffectsSettings: ISiteEffectsSettings = {
  backcolour: "#000000",
  backgroundModes: ["arms", "rings"],
  siteFxCategories: ["all"],
  images: {
    enabled: false,
    clickable: true,
    corruption: false,
    urls: [],
    perMin: 2,
    imagesPerFlash: 3,
    maxOnScreen: 8,
    imageDurationSec: 30,
  },
  videos: {
    enabled: false,
    strictLock: false,
    urls: [],
    perHour: 2,
    miniGameEnabled: false,
    miniGameTargets: 4,
    miniGameDurationSec: 10,
    miniGameTargetSizePx: 72,
    miniGameButtonMessages: ["Tap now", "Still watching?", "Click to continue"],
  },
  audio: {
    enabled: false,
    perHour: 2,
    urls: [],
  },
  messages: {
    enabled: false,
    messages: [],
    perMin: 2,
    frames: 3,
    opacityPercent: 90,
    messageDurationMinMs: 200,
    messageDurationMaxMs: 200,
  },
};

export interface IGeneralSettings {
  adminLockPassword?: string | undefined;
  backgroundColor: string | undefined;
  applicationTitle: string | undefined;
  dailyRulesText?: string;
  dailyCost: number;
  randomiseSettings: boolean;
  showRemainingTimes: boolean;
  showBank: boolean;
  minReportTime: string;
  maxReportTime: string;
  reportWordCount: number;
  dailyReporting: boolean;
  dailyReportRequest: IReportRequest | undefined;
  morningReportRequest: IReportRequest | undefined;
  dailyReportingCredit: number;
  weekendHandling: WeekendHandling;
  randomVerifications: boolean;
  verificationLimit?: number;
  penaltyPointConversionType: PenaltyPointConversionType;
  version?: string;
  holidayMode?: boolean;
  holidayEndDate?: Date;
  reportStyle?: ReportStyle;
  disableDiscordProof?: boolean;
  disableDiscordShaming?: boolean;
  disableDiscordTicker?: boolean;
  hygieneBreaks?: HygieneBreak;
  hygieneIntervalMins?: number;
  hygienePillories?: string[];
  hygienePenalties?: string[];
  hygieneApplyCostPerMin?: boolean;
  hygieneCostPerMin?: number;
  linesGameplayModeDefault?: LinesGameplayMode;
  hidePendingTasks?: boolean;
  customScheduledDays?: Record<EventDay, string>;
  customScheduledDates?: Record<string, string>;
  siteEffectsSettings?: ISiteEffectsSettings;
}

// these are specific to the session settings being currently run
export interface ISessionSettings extends IGeneralSettings {
  creditSettings: ITaskSettings;
  penaltySettings: IPenaltySettings;
  permissionSettings: IPermissionSettings;
  eventsSettings: IEventSettings;
  positionSettings: IPositionSettings;
  userid?: string;
}

export interface IOwnerProfileSession extends IUserPreferences {
  hidden: boolean;
  hintCategories: string[];
  name: string;
  ownerImg: string;
  description: string;
  welcomeMessage?: string;
  lockOnSelection: boolean;
  lockType?: LockTypes;
  lockCountType?: LockCountType;
  lockCount?: number;
  strictness: Strictness;
  score: number;
  userid: string;
  username: string;
  isSystem?: boolean;
  chastityOnly?: boolean;
  templateForOwners?: boolean;
}

interface IAuditBankItem {
  time: Date;
  bank: number;
  reason: string;
}

interface IAuditLogRecordCollection {
  [audits: string]: AuditMessage;
}
interface IAuditBankRecordCollection {
  [audits: string]: IAuditBankItem;
}
export interface IAuditBankDateCollection {
  [dates: string]: IAuditBankRecordCollection;
}
export interface IAuditLogsDateCollection {
  [dates: string]: IAuditLogRecordCollection;
}

export interface IAuditScoreHistory {
  date: string;
  score: number;
  bank: number;
  penalties: number;
}

export interface IEventDecisionResponse {
  approvalId?: string;
  itemKey: string;
  decision: boolean;
  decisionResponse?: IEventCustomDecisionResponse;
}

export interface MessageContents {
  subject: string;
  message: string;
  fromUsername: string;
  fromUserid: string;
  toUsername: string;
  messageType?: MessageType;
  state?: any;
}

export interface IMemberSentReceipt extends IKeyedItem {
  toUsername: string;
  sent: Date;
  readDate?: Date;
  subject: string;
  read: boolean;
  message: string;
}

export interface IMemberMessage extends IKeyedItem {
  message: MessageContents;
  read: boolean;
  keep: boolean;
  sent: Date;
}

export interface IHelpTopic {
  content: string;
  title: string;
  image?: string | string[];
  isNew?: boolean;
  isPinned?: boolean;
  key?: string;
  topicGroup?: string;
  video?: string;
}

export interface IOwnerHistory {
  date: Date;
  ownerName: string;
  type: string;
}

export class AuditMessage {
  level: TraceLevel;
  userid?: string;
  username?: string;
  id: string;
  time: Date;
  localeTime: string;
  type: AuditActionTypes;
  name: string;
  action: string;
  success?: boolean;
  cost?: number;

  constructor(
    type: AuditActionTypes,
    name: string,
    action: string,
    level: TraceLevel,
    success = false,
    cost = 0
  ) {
    this.id = UUID();
    this.time = new Date();
    this.localeTime = this.time.toLocaleString();
    this.type = type;
    this.name = name;
    this.action = action;
    this.success = success;
    this.cost = cost;
    this.level = level;
  }
}

export class UserActionAuditMessage extends AuditMessage {
  constructor(type: AuditActionTypes, name: string, action: string) {
    super(type, name, action, TraceLevel.info);
  }
}

export interface IUserRecord {
  userid: string;
  username: string;
}

export interface CookieShape extends IUserCredentials, IUserRecord {
  tempEmail?: string;
  acceptTerms?: boolean;
  autoLogon?: boolean;
}

interface IUserCredentials {
  password: string;
  username: string;
}

interface IUserPreferences {
  categories?: string[];
  missingCategories?: string[];
  mutedMessageTypes?: MessageType[] | null;
  mutedNotificationTypes?: NotificationType[] | null;
}

interface ILocaleInfo {
  country?: string;
  discordUserId?: string;
  language?: string[];
}

interface IMemberProfile extends IUserRecord, IUserPreferences, ILocaleInfo {
  alias?: string;
  isAdmin: boolean;
  profileImage?: string | null;
  role?: ProfileRole;
  summary?: string;
  location?: Record<string, string>;
  disablePublicProofs?: boolean;
  ownerHistory?: IOwnerHistory[];
}

export interface IOwnershipControl extends IUserRecord {
  virtualProfileId?: string;
  virtualProfileName?: string;
  virtualProfileImage?: string;
  owner?: string;
  ownerid?: string;
  loanedOwner?: string;
  loanedOwnerid?: string;
  otherOwnerIds?: string[];
}

export interface ISessionProgressState {
  bank: number;
  penaltyPoints: number;
  lastScore?: number;
  totalScore?: number;
  lastPenalties?: number;
  totalPenalties?: number;
}

export interface IControl
  extends IUserPreferences,
  ISessionProgressState,
  IOwnershipControl,
  IMemberProfile {
  created?: Date;
  lastUpdateTime: Date;
  heartbeat: string;
  isAdmin: boolean;
  isActive?: boolean;
  lovenseSync?: boolean;
  displayMode?: string;
  ownerHistory?: IOwnerHistory[];
}

export class ControlRecord implements IPictureItem, IControl {
  constructor(user: IControl) {
    this.userid = user.userid ?? "";
    this.username = user.username ?? "";
    this.alias = user.alias;
    this.created = user.created;
    this.lastUpdateTime = new Date(user.lastUpdateTime);
    this.bank = user.bank;
    this.heartbeat = user.heartbeat;
    this.isAdmin = user.isAdmin;
    this.isActive = user.isActive;
    this.virtualProfileId = user.virtualProfileId;
    this.virtualProfileName = user.virtualProfileName;
    this.virtualProfileImage = user.virtualProfileImage;
    this.owner = user.owner;
    this.ownerid = user.ownerid;
    this.loanedOwner = user.loanedOwner;
    this.loanedOwnerid = user.loanedOwnerid;
    this.otherOwnerIds = user.otherOwnerIds;
    this.profileImage = (user as any).memberImage || user.profileImage;
    this.mutedMessageTypes = user.mutedMessageTypes;
    this.mutedNotificationTypes = user.mutedNotificationTypes;
    this.role = user.role;
    this.summary = user.summary;
    this.categories = user.categories;
    this.missingCategories = user.missingCategories;
    this.lastScore = user.lastScore;
    this.lastPenalties = user.lastPenalties;
    this.totalScore = user.totalScore;
    this.totalPenalties = user.totalPenalties;
    this.penaltyPoints = user.penaltyPoints;
    this.displayMode = user.displayMode;
    this.location = user.location;

    if (!this.location) this.location = {};
    if (Array.isArray(this.location))
      this.location = {
        city: (this.location as string[])[0] ?? "",
        region: (this.location as string[])[1] ?? "",
        country_name: (this.location as string[])[2] ?? "",
        longitude: (this.location as string[])[3] ?? "",
        latitude: (this.location as string[])[4] ?? "",
      };

    this.disablePublicProofs = user.disablePublicProofs;
    this.lovenseSync = user.lovenseSync;

    this.country = user.country;
    this.discordUserId = user.discordUserId;
    this.language = Array.isArray(user.language) ? user.language : [];
    this.ownerHistory = user.ownerHistory;
  }
  userid: string;
  username: string;
  alias?: string;
  displayMode?: string;
  lastUpdateTime: Date;
  created?: Date;
  bank: number;
  penaltyPoints: number;
  heartbeat: string;
  isAdmin: boolean;
  isActive?: boolean;

  // the template last selected by the user
  virtualProfileId?: string;
  virtualProfileName?: string;
  virtualProfileImage?: string;

  // the real owner of the user
  owner?: string;
  ownerid?: string;
  // the current loaned owner of the user
  loanedOwner?: string;
  loanedOwnerid?: string;
  otherOwnerIds?: string[];
  profileImage?: string | null;
  memberImage?: string;
  mutedMessageTypes?: MessageType[] | null;
  mutedNotificationTypes?: NotificationType[] | null;
  role?: ProfileRole;
  summary?: string;
  categories?: string[];
  missingCategories?: string[];
  lastScore?: number;
  lastPenalties?: number;
  totalScore?: number;
  totalPenalties?: number;
  location?: Record<string, string>;
  disablePublicProofs?: boolean;
  lovenseSync?: boolean;
  country?: string;
  discordUserId?: string;
  language?: string[];
  ownerHistory?: IOwnerHistory[];

  public get img(): string {
    return getProfileImage(this);
  }
  public get imgId(): string {
    return "";
  }

  public ownerHasAccess = (ownerRecord: IUserRecord) => {
    const ownerid = ownerRecord.userid;
    return (
      this.ownerid === ownerid ||
      this.loanedOwnerid === ownerid ||
      (this.otherOwnerIds && this.otherOwnerIds.includes(ownerid))
    );
  };

  mergeSessionProgressStateChange = (
    sessionStateChange: ISessionProgressState
  ) => {
    if (!sessionStateChange) return;
    this.bank = sessionStateChange.bank;
    this.lastPenalties = sessionStateChange.lastPenalties;
    this.lastScore = sessionStateChange.lastScore;
    this.penaltyPoints = sessionStateChange.penaltyPoints;
    this.totalPenalties = sessionStateChange.totalPenalties;
    this.totalScore = sessionStateChange.totalScore;
  };

  mergeOwnershipRecord = (ownershipControl: IOwnershipControl) => {
    if (!ownershipControl) return;
    this.loanedOwner = ownershipControl.loanedOwner;
    this.loanedOwnerid = ownershipControl.loanedOwnerid;
    this.otherOwnerIds = ownershipControl.otherOwnerIds;
    this.owner = ownershipControl.owner;
    this.ownerid = ownershipControl.ownerid;
    this.virtualProfileId = ownershipControl.virtualProfileId;
    this.virtualProfileImage = ownershipControl.virtualProfileImage;
    this.virtualProfileName = ownershipControl.virtualProfileName;
  };

  getOwner = (): IUserRecord => {
    // if loaned then owner is
    if (this.loanedOwnerid)
      return {
        userid: this.loanedOwnerid,
        username: this.loanedOwner as string,
      };

    if (this.ownerid)
      return {
        userid: this.ownerid,
        username: this.owner as string,
      };

    if (this.virtualProfileName)
      return {
        userid: this.virtualProfileId ?? this.userid,
        username: this.virtualProfileName,
      };

    return {
      userid: this.userid ?? "",
      username: this.username ?? "",
    };
  };

  isOwnerProfile = () => this.userid?.endsWith(memberOwnerKeySuffix);

  getOwnerType = (): OwnerType => {
    if (this.hasVirtualOwner()) return OwnerType.virtual;
    if (this.isOwnedBySelf()) return OwnerType.unowned;
    if (this.loanedOwnerid) return OwnerType.loaned;
    return OwnerType.owned;
  };

  // do you own your settings
  isOwnedBySelf = (): boolean => {
    return this.hasVirtualOwner() || this.getOwner().userid === this.userid;
  };

  // are you able to update a setting based on who you are and who the owner is
  canOwnSettings = (loggedOnUser: IUserRecord, isSystem: boolean): boolean => {
    // you are looking at your own
    if (this.isOwnedBySelf()) return true;
    // already owned by another
    if (this.ownerHasAccess(loggedOnUser)) return true;
    // already owned by you
    if (this.getOwner().userid === loggedOnUser.userid) return true;
    // or we are the system
    if (isSystem) return true;
    // has been shared to you
    return false;
  };

  // checks is owner is owner of a user
  isOwnedBy = (loggedOnUser: Cookies, isSystem: boolean): boolean => {
    if (isSystem) return true;
    return (
      this.isOwnedBySelf() ||
      (this.otherOwnerIds &&
        this.otherOwnerIds.includes(loggedOnUser.userid)) ||
      this.ownerid === loggedOnUser.userid ||
      this.loanedOwnerid === loggedOnUser.userid
    );
  };

  isOwned = (): boolean => {
    return !this.isOwnedBySelf();
  };

  hasVirtualOwner = (): boolean => {
    // if loaned then owner is
    if (this.loanedOwnerid) return false;
    if (this.ownerid) return false;
    if (this.virtualProfileName) return true;
    return false;
  };

  isSettingsOwner = (loggedOnUser: Cookies, isSystem: boolean): boolean => {
    return (
      isSystem || this.isOwnedBySelf() || this.isOwnedBy(loggedOnUser, isSystem)
    );
  };
}

export abstract class BaseBasketItem implements IBasketInfo, IScheduledInfo {
  constructor(basketItem: IBasketInfo) {
    this.key = basketItem.key;
    this.itemType = basketItem.itemType;
    this.description = basketItem.description;
    this.hidden = basketItem.hidden;
    this.img = basketItem.img;
    this.imgId = basketItem.imgId;
    this.name = basketItem.name;
    this.isPaused = basketItem.isPaused;
    this.isComplete = basketItem.isComplete;
    this.availabilityDayType = basketItem.availabilityDayType;
    this.scheduleReason = basketItem.scheduleReason;
    this.completedTime = basketItem.completedTime;
    this.requestedByKey = basketItem.requestedByKey;
    this.requestedByName = basketItem.requestedByName;
    this.customScheduleKey = basketItem.customScheduleKey;
    this.autoStartOnRequestedByStart = basketItem.autoStartOnRequestedByStart;
    this.autoStartedByRequestedBy = basketItem.autoStartedByRequestedBy;
  }
  completedTime?: Date;
  isPaused: boolean;
  isComplete: boolean;
  key: string;
  itemType: ItemTypes;
  description: string;
  hidden?: boolean | undefined;
  img?: string;
  imgId: string | undefined;
  name: string;
  availabilityDayType: AvailabilityDayType;
  scheduleReason: string;
  requestedByKey?: string;
  requestedByName?: string;
  customScheduleKey?: string;
  autoStartOnRequestedByStart?: boolean;
  autoStartedByRequestedBy?: boolean;

  abstract getBasketText: (
    checkEventComplete: (item: IEndEventTriggered) => boolean
  ) => string;
}

export abstract class BaseTimedBasketItem extends BaseBasketItem {
  constructor(timerInfo: ITimedBasketInfo) {
    super(timerInfo as IBasketInfo);
    this.timerTicks = timerInfo.timerTicks;
    this.countType = timerInfo.countType;
    this.expectedCount = timerInfo.expectedCount;
    this.scheduleReason = timerInfo.scheduleReason;
  }
  timerTicks?: number;
  countType: CountType;
  scheduleReason: string;
  expectedCount?: number;
  startedTime?: Date;
  abstract get remainingMinutes(): string | undefined;
}

interface IBasketInfo extends ISettingsItem, IScheduledInfo {
  isPaused: boolean;
  isComplete: boolean;
  completedTime?: Date;
}

export interface ITimedBasketInfo extends IBasketInfo {
  timerTicks?: number;
  countType: CountType;
  expectedCount?: number;
  startedTime?: Date;
}

export class ScheduleBasketItem extends BaseBasketItem {
  constructor(scheduleItem: ISchedulerTimerInfo) {
    super(scheduleItem); // either done or not, no timer
    this.startTime = scheduleItem.startTime;
    this.missEventPenalties = scheduleItem.missEventPenalties;
    this.missEventPillories = scheduleItem.missEventPillories;
  }
  getBasketText = (): string => {
    return this.name;
  };
  startTime?: Date;
  missEventPenalties?: string[];
  missEventPillories?: string[];
}

export class PositionBasketItem
  extends BaseTimedBasketItem
  implements IPositionBasketInfo, IPercentComplete {
  constructor(positionInfo: IPositionBasketInfo, positionTimerTicks: number) {
    super(positionInfo);
    this.endTime = positionInfo.endTime;
    this.expectedCount = positionInfo.expectedCount;
    this.initialCount = positionInfo.initialCount;
    this.hasPosition = positionInfo.hasPosition;
    this.timerTicks = isNaN(positionTimerTicks) ? 0 : positionTimerTicks;
    this.isComplete = this.expectedCount === 0;
    this.isPaused = !this.isComplete && !this.endTime;
    this.duration = positionInfo.duration;
    this.hasMetronome = positionInfo.hasMetronome;
    this.hasSiteEffects = positionInfo.hasSiteEffects;
    this.taskKey = positionInfo.taskKey;
    this.audioUrl = positionInfo.audioUrl;
    this.intervalMs = positionInfo.intervalMs;
    this.siteEffectsSettings = positionInfo.siteEffectsSettings;
  }

  get remainingMinutes(): string | undefined {
    if (this.timerTicks && this.timerTicks > 0)
      return convertToMinutes(this.timerTicks);
    return "Complete";
  }

  get percentComplete(): number {
    if (this.isComplete) return 0;
    if (this.isPaused) return 1;
    let percent = 0;
    if (this.duration && this.timerTicks) {
      percent = this.timerTicks / this.duration;
    }
    return percent > 1 ? 1 : percent;
  }

  countType = CountType.minutes;
  expectedCount: number;
  initialCount: number;
  duration?: number;
  endTime?: Date;
  hasPosition: boolean;
  hasMetronome?: boolean;
  hasSiteEffects?: boolean;
  taskKey?: string;
  audioUrl?: string;
  intervalMs?: number;
  siteEffectsSettings?: ISiteEffectsSettings;

  getBasketText = (): string => {
    return `${this.name}${this.isComplete ? " Done" : ""}`;
  };
}

/// concrete impl of the Task item for a credit
export class TaskBasketItem
  extends BaseTimedBasketItem
  implements ITaskTimerInfo, IPercentComplete, IEndEventTriggered, ILovenseToy {
  constructor(taskTimer: ITaskTimerInfo) {
    super(taskTimer);
    this.isItem = taskTimer.isItem;
    this.selectedCondition = taskTimer.selectedCondition;
    this.selectedChoiceName = taskTimer.selectedChoiceName;
    this.selectedItems = taskTimer.selectedItems;
    this.reason = taskTimer.reason;
    this.ownerAdded = taskTimer.ownerAdded;
    this.ownerName = taskTimer.ownerName;
    this.isRoulette = taskTimer.isRoulette;
    this.isPillory = taskTimer.isPillory;
    this.permanent = taskTimer.permanent;
    this.countType = taskTimer.countType;
    this.credit = taskTimer.credit;
    this.endTriggerEventName = taskTimer.endTriggerEventName;
    this.endTriggerEvent = taskTimer.endTriggerEvent;
    this.endTime = taskTimer.endTime;
    this.startedTime = taskTimer.startedTime;
    this.creditTaskType = taskTimer.creditTaskType ?? TaskType.standard;
    this.alwaysShowTime = taskTimer.alwaysShowTime;
    this.categories = taskTimer.categories ?? [];
    this.taskKeys = taskTimer.taskKeys ?? [];
    this.penaltyKeys = taskTimer.penaltyKeys ?? [];
    this.pilloryKeys = taskTimer.pilloryKeys ?? [];
    this.taskVotes = taskTimer.taskVotes ?? [];
    this.votable = taskTimer.votable;
    this.preSelect = taskTimer.preSelect;
    this.lovenseProfile = taskTimer.lovenseProfile;
    this.voteCount = taskTimer.voteCount;
    this.lovenseToyId = taskTimer.lovenseToyId;
    this.isLovenseToy = taskTimer.isLovenseToy;
    this.requiresOwner = taskTimer.requiresOwner;
    this.proofStatus = taskTimer.proofStatus ?? ProofStatus.random;
    this.weeklyTargetValue = taskTimer.weeklyTargetValue;
    this.weeklyTargetUnit = taskTimer.weeklyTargetUnit;
    this.weeklyTargetPenaltyKey = taskTimer.weeklyTargetPenaltyKey;
    this.webLinkOptions = taskTimer.webLinkOptions;
    this.rollOverEventDays = taskTimer.rollOverEventDays;
    this.timesRequired = taskTimer.timesRequired ?? 1;
    this.monitoredTimer = taskTimer.monitoredTimer;
  }
  get expireOnPermission(): boolean {
    return this.endTriggerEvent !== undefined;
  }

  get isLimited(): boolean {
    return this.expectedCount && this.expectedCount > 0 ? true : false;
  }

  get remainingMinutes(): string | undefined {
    if (this.timerTicks && this.timerTicks > 0)
      return convertToCountTypeString(this.timerTicks, CountType.minutes);
    return "Complete";
  }

  get percentComplete(): number {
    if (this.isComplete) return 1;
    if (this.endTriggerEvent) return 0;
    let percent = 0;
    if (this.expectedCount && this.timerTicks) {
      percent = this.timerTicks / this.expectedCount;
    }
    return percent > 1 ? 1 : percent;
  }

  rollOverEventDays?: boolean | undefined;
  webLinkOptions?: boolean;
  isItem?: boolean;
  isPenalty = false;
  reason?: string;
  selectedCondition?: string;
  selectedChoiceName?: string;
  taskKeys?: string[];
  penaltyKeys?: string[];
  pilloryKeys?: string[];
  taskVotes?: ITaskVoteConfig[];
  ownerAdded?: boolean;
  ownerName?: string;
  isRoulette?: boolean;
  isPillory?: boolean;
  permanent?: boolean;
  credit: number;
  endTriggerEventName?: string;
  endTriggerEvent?: string;
  endTime?: Date;
  categories: string[];
  selectedItems?: Record<string, string>; // [itemName] [- condition if any]
  creditTaskType?: TaskType;
  alwaysShowTime?: boolean; // ignore the hide timer setting for items which need to see time to work properly
  votable?: boolean;
  preSelect: PreselectType;
  lovenseToyId?: string;
  lovenseProfile?: LovensePresetName;
  voteCount?: number;
  isLovenseToy?: boolean;
  requiresOwner?: boolean;
  proofStatus?: ProofStatus;
  weeklyTargetValue?: number;
  weeklyTargetUnit?: string;
  weeklyTargetPenaltyKey?: string;
  monitoredTimer?: boolean;
  // tasks can be delayed to later in the day, in which case we might have the time it is due here
  startTime?: Date;
  timesRequired: number;

  decrementTimesRemaining = () => {
    if (this.timesRequired > 0) this.timesRequired -= 1;
    return this.timesRequired > 0;
  };
  decrementCountRequired = () => {
    if (!this.expectedCount) return;
    if (this.expectedCount > 0) this.expectedCount -= 1;
    return this.expectedCount > 0;
  };

  getBasketText = (
    checkEventComplete: (item: IEndEventTriggered) => boolean
  ): string => {
    if (this.creditTaskType === TaskType.acknowledgement) return this.name; // all in the name when an ack task
    let text = this.name;
    if (this.selectedCondition) {
      text += this.selectedCondition ? ` - ${this.selectedCondition}` : "";
    }
    if (this.countType && !isTimedItem(this.countType) && this.expectedCount) {
      text += ` ${convertToCountTypeString(
        this.expectedCount,
        this.countType
      )}`;
    }
    if (
      this.creditTaskType === TaskType.weeklyNumericTarget &&
      this.weeklyTargetValue &&
      this.weeklyTargetValue > 0
    ) {
      const unit = this.weeklyTargetUnit ? ` ${this.weeklyTargetUnit}` : "";
      text += ` - Weekly Target: ${this.weeklyTargetValue}${unit}`;
    }
    if (this.timesRequired > 1) {
      text += ` (${this.timesRequired} Times)`;
    }
    if (this.reason) {
      text += ` (${this.reason})`;
    }
    if (this.isPillory) text += " - From Pillory";
    if (this.isRoulette) text += " - From WoF";
    if (this.ownerAdded && !this.ownerName) text += " - From Owner";
    if (this.ownerAdded && this.ownerName) text += " - From " + this.ownerName;
    if (
      this.endTriggerEventName &&
      !this.isComplete &&
      this.isPaused &&
      checkEventComplete(this) &&
      !this.permanent
    )
      text += ` - Until ${this.endTriggerEventName}`;
    return text;
  };
  /**
   * Does this item need a roulette to choose the option to fire
   */
  public static NeedsRouletteToChooseOption(
    taskBasketItem: ITaskTimerInfo | IPenaltyTimerInfo,
    task: IOptionSupport
  ): boolean {
    return (
      task &&
      task.options !== undefined &&
      task.options.filter((o) => o).length > 0 &&
      !taskBasketItem.selectedCondition
    );
  }
}

export interface IPercentComplete {
  percentComplete: number;
}

export interface IEndEventTriggered extends INamedItem {
  endTriggerEventName?: string;
  endTriggerEvent?: string;
  rollOverEventDays?: boolean;
}

/// concrete impl of the Task item for a credit
export class PenaltyBasketItem
  extends BaseTimedBasketItem
  implements
  IPercentComplete,
  IPenaltyTimerInfo,
  IEndEventTriggered,
  ILovenseToy,
  IOwnerRequired {
  constructor(penaltyTimer: IPenaltyTimerInfo) {
    super(penaltyTimer);
    this.isItem = penaltyTimer.isItem;
    this.selectedCondition = penaltyTimer.selectedCondition;
    this.reason = penaltyTimer.reason;
    this.ownerAdded = penaltyTimer.ownerAdded;
    this.ownerName = penaltyTimer.ownerName;
    this.countType = penaltyTimer.countType;
    this.penaltyType = penaltyTimer.penaltyType ?? PenaltyType.task;
    this.endTriggerEventName = penaltyTimer.endTriggerEventName;
    this.endTriggerEvent = penaltyTimer.endTriggerEvent;
    this.endTime = penaltyTimer.endTime;
    this.startedTime = penaltyTimer.startedTime;
    this.categories = penaltyTimer.categories ?? [];
    this.isPillory = penaltyTimer.isPillory;
    this.isRoulette = penaltyTimer.isRoulette;
    this.taskKeys = penaltyTimer.taskKeys ?? [];
    this.penaltyKeys = penaltyTimer.penaltyKeys ?? [];
    this.pilloryKeys = penaltyTimer.pilloryKeys ?? [];
    this.taskVotes = penaltyTimer.taskVotes ?? [];
    this.alwaysShowTime = penaltyTimer.alwaysShowTime;
    this.points = penaltyTimer.points;
    this.options = penaltyTimer.options;
    this.optionImages = penaltyTimer.optionImages;
    this.votable = penaltyTimer.votable;
    this.lovenseProfile = penaltyTimer.lovenseProfile;
    this.voteCount = penaltyTimer.voteCount;
    this.lovenseToyId = penaltyTimer.lovenseToyId;
    this.isLovenseToy = penaltyTimer.isLovenseToy;
    this.requiresOwner = penaltyTimer.requiresOwner;
    this.proofStatus = penaltyTimer.proofStatus ?? ProofStatus.random;
    this.webLinkOptions = penaltyTimer.webLinkOptions;
    this.rollOverEventDays = penaltyTimer.rollOverEventDays;
    this.selectedItems = penaltyTimer.selectedItems;
    this.monitoredTimer = penaltyTimer.monitoredTimer;
  }

  get expireOnPermission(): boolean {
    return this.endTriggerEvent !== undefined;
  }

  get isLimited(): boolean {
    return this.expectedCount && this.expectedCount > 0 ? true : false;
  }

  get remainingMinutes(): string | undefined {
    if (this.timerTicks && this.timerTicks > 0)
      return convertToCountTypeString(this.timerTicks, CountType.minutes);
    return "Complete";
  }

  get percentComplete(): number {
    if (this.isComplete) return 1;
    if (this.endTriggerEvent) return 0;
    let percent = 0;
    if (this.expectedCount && this.timerTicks) {
      percent = this.timerTicks / this.expectedCount;
    }
    return percent > 1 ? 1 : percent;
  }

  rollOverEventDays?: boolean | undefined;
  webLinkOptions?: boolean;
  isItem?: boolean;
  isPenalty = true;
  taskKeys?: string[];
  penaltyKeys?: string[];
  pilloryKeys?: string[];
  taskVotes?: ITaskVoteConfig[];
  selectedCondition?: string;
  reason?: string;
  ownerAdded?: boolean;
  ownerName?: string;
  penaltyType: PenaltyType;
  endTriggerEventName?: string;
  endTriggerEvent?: string;
  endTime?: Date;
  categories: string[];
  isPillory?: boolean;
  isRoulette?: boolean;
  alwaysShowTime?: boolean; // ignore the hide timer setting for items which need to see time to work properly
  points: number;
  options: string[];
  optionImages?: IPictureItem[];
  votable?: boolean;
  lovenseToyId?: string;
  lovenseProfile?: LovensePresetName;
  voteCount?: number;
  isLovenseToy?: boolean;
  requiresOwner?: boolean;
  proofStatus?: ProofStatus;
  monitoredTimer?: boolean;
  selectedItems?: Record<string, string>; // [itemName] [- condition if any]
  // tasks can be delayed to later in the day, in which case we might have the time it is due here
  startTime?: Date;

  getBasketText = (
    checkEventComplete: (item: IEndEventTriggered) => boolean
  ): string => {
    let text = this.name;
    if (this.penaltyType === PenaltyType.acknowledgement) return this.name; // all in the name when an ack task
    if (
      !isOutfitItem(this) ||
      !this.selectedItems ||
      Object.keys(this.selectedItems).length === 0
    ) {
      text += this.selectedCondition ? ` - ${this.selectedCondition}` : "";
    }
    if (
      this.countType &&
      !isTimedItem(this.countType) &&
      this.expectedCount &&
      this.expectedCount > 1
    ) {
      text += ` ${convertToCountTypeString(
        this.expectedCount,
        this.countType
      )}`;
    }
    if (this.reason) {
      text += ` (${this.reason})`;
    }
    if (this.isPillory) text += " - From Pillory";
    if (this.isRoulette) text += " - From WoF";
    if (this.ownerAdded && !this.ownerName) text += " - From Owner";
    if (this.ownerAdded && this.ownerName) text += " - From " + this.ownerName;
    if (
      this.endTriggerEventName &&
      checkEventComplete(this) &&
      this.isPaused &&
      !this.isComplete
    )
      text += ` - Until ${this.endTriggerEventName}`;
    return text;
  };
}

/// the timer interface of the events
export interface IPermissionTimerInfo extends ITimedBasketInfo {
  decision: boolean;
  reason?: string;
  selectedControlType: ControlType;
  selectedCondition?: string;
  isTimed: boolean;
  duration?: Date;
  endTime?: Date;
  expired: boolean;
  exclude: boolean;
  taskKeys?: string[];
  selectedTaskKeys?: string[];
  taskCategories?: string[];
  penalties?: string[];
  // allows tasks fired by events to run until another event - overriding any timers they may have. only makes sense for tasks and categories
  tasksEndTriggerEvent?: string;
  requiredTasksInProgress?: string[];
  pickSingleTask?: boolean;
  pickSinglePenalty?: boolean;
  webLinkOptions?: boolean;
  pickSingleCategoryTask?: boolean;
}

/// the timer interface of the events
export interface IEventTimerInfo extends IBasketInfo {
  decision: boolean;
  reason?: string;
  exclude: boolean;
  isReport?: boolean;
  taskKeys?: string[];
  selectedTaskKeys?: string[];
  taskCategories?: string[];
  penalties?: string[];
  eventType: EventType;
  tasksEndTriggerEvent?: string;
  requiredTasksInProgress?: string[];
  pickSingleTask?: boolean;
  pickSinglePenalty?: boolean;
  pickSingleCategoryTask?: boolean;
  selectedCondition?: string;
  webLinkOptions?: boolean;
  itemOptions?: Record<string, string>;
  runTasksToNextEvent?: boolean;
  taskDurationMinutes?: number;
  awaitTaskComplete?: boolean;
}

/// concrete impl of the Task item for an event
export class EventBasketItem extends BaseBasketItem implements IEventTimerInfo {
  constructor(eventTimer: IEventTimerInfo) {
    super(eventTimer);
    this.decision = eventTimer.decision;
    this.reason = eventTimer.reason;
    this.exclude = eventTimer.exclude;
    this.isReport = eventTimer.isReport;
    this.taskKeys = eventTimer.taskKeys;
    this.selectedTaskKeys = eventTimer.selectedTaskKeys;
    this.taskCategories = eventTimer.taskCategories;
    this.eventType = eventTimer.eventType;
    this.tasksEndTriggerEvent = eventTimer.tasksEndTriggerEvent;
    this.requiredTasksInProgress = eventTimer.requiredTasksInProgress;
    this.penalties = eventTimer.penalties;
    this.pickSinglePenalty = eventTimer.pickSinglePenalty;
    this.pickSingleCategoryTask = eventTimer.pickSingleCategoryTask;
    this.pickSingleTask = eventTimer.pickSingleTask;
    this.selectedCondition = eventTimer.selectedCondition;
    this.webLinkOptions = eventTimer.webLinkOptions;
    this.itemOptions = eventTimer.itemOptions;
    this.runTasksToNextEvent = eventTimer.runTasksToNextEvent;
    this.taskDurationMinutes = eventTimer.taskDurationMinutes;
    this.awaitTaskComplete = eventTimer.awaitTaskComplete;
  }

  webLinkOptions?: boolean;
  decision: boolean;
  reason?: string;
  exclude: boolean;
  isReport?: boolean;
  taskKeys?: string[];
  selectedTaskKeys?: string[];
  eventType: EventType;
  requiredTasksInProgress?: string[];
  taskCategories?: string[];
  penalties?: string[];
  tasksEndTriggerEvent?: string;
  pickSingleTask?: boolean;
  pickSinglePenalty?: boolean;
  pickSingleCategoryTask?: boolean;
  selectedCondition?: string;
  itemOptions?: Record<string, string>;
  runTasksToNextEvent?: boolean;
  taskDurationMinutes?: number;
  awaitTaskComplete?: boolean;

  // returns a description for this event based on it's settings.
  getBasketText = (): string => {
    const itemName = this.name;
    let result = "";
    if (!this.decision) {
      if (!this.reason) {
        result = `${itemName} is denied today`;
      } else result = `${itemName} - ${this.reason}`;
    } else {
      result = `${itemName}`;
      result += " approved";
      if (this.reason) {
        result += ` (${this.reason})`;
      }
      if (this.selectedCondition) {
        result += ` (${this.selectedCondition})`;
      }
    }
    return result;
  };
}

/// concrete impl of the Task item for an event
export class PermissionBasketItem
  extends BaseTimedBasketItem
  implements IPermissionTimerInfo, IPercentComplete {
  constructor(permissionTimer: IPermissionTimerInfo) {
    super(permissionTimer);
    this.decision = permissionTimer.decision;
    this.reason = permissionTimer.reason;
    this.selectedControlType = permissionTimer.selectedControlType;
    this.isTimed = permissionTimer.isTimed;
    this.duration = permissionTimer.duration;
    this.endTime = permissionTimer.endTime;
    this.startedTime = permissionTimer.startedTime;
    this.expired = permissionTimer.expired;
    this.exclude = permissionTimer.exclude;
    this.countType = permissionTimer.countType;
    this.selectedCondition = permissionTimer.selectedCondition;
    this.taskCategories = permissionTimer.taskCategories;
    this.tasksEndTriggerEvent = permissionTimer.tasksEndTriggerEvent;
    this.requiredTasksInProgress = permissionTimer.requiredTasksInProgress;
    this.penalties = permissionTimer.penalties;
    this.pickSinglePenalty = permissionTimer.pickSinglePenalty;
    this.pickSingleCategoryTask = permissionTimer.pickSingleCategoryTask;
    this.pickSingleTask = permissionTimer.pickSingleTask;
    this.webLinkOptions = permissionTimer.webLinkOptions;
    this.selectedTaskKeys = permissionTimer.selectedTaskKeys;
  }

  get remainingMinutes(): string | undefined {
    if (this.timerTicks && this.timerTicks > 0)
      return convertToCountTypeString(this.timerTicks, CountType.minutes);
    return "Complete";
  }

  get percentComplete(): number {
    if (this.expired || this.isComplete) return 1;
    let percent = 0;
    if (this.expectedCount && this.timerTicks) {
      percent = this.timerTicks / this.expectedCount;
    }
    return percent > 1 ? 1 : percent;
  }

  webLinkOptions?: boolean;
  decision: boolean;
  reason?: string;
  selectedCondition?: string;
  selectedControlType: ControlType;
  isTimed: boolean;
  duration?: Date;
  endTime?: Date;
  expired: boolean;
  exclude: boolean;
  taskKeys?: string[];
  selectedTaskKeys?: string[];
  taskCategories?: string[];
  penalties?: string[];
  tasksEndTriggerEvent?: string;
  requiredTasksInProgress?: string[];
  pickSingleTask?: boolean;
  pickSinglePenalty?: boolean;
  pickSingleCategoryTask?: boolean;

  // returns a description for this event based on it's settings.
  getBasketText = (): string => {
    const itemName = this.name;
    if (this.isTimed && this.expired) return `${itemName} expired for today`;

    let result = "";
    if (!this.decision) {
      if (!this.reason) {
        result = `${itemName} is denied today`;
      } else result = `${itemName} - ${this.reason}`;
    } else {
      result = `${itemName}`;
      if (this.isTimed && !this.isPaused && this.timerTicks) {
        result += " approved until expiry";
      } else if (
        this.isTimed &&
        !this.timerTicks &&
        this.expectedCount &&
        !isNaN(this.expectedCount)
      ) {
        result += " denied (expired)";
      } else if (
        this.countType &&
        !this.isTimed &&
        this.expectedCount &&
        !isNaN(this.expectedCount)
      ) {
        if (this.selectedControlType === ControlType.exactly)
          result += ` exactly ${convertToCountTypeString(
            this.expectedCount,
            this.countType
          )} approved`;
        else if (this.selectedControlType === ControlType.minimum)
          result += ` over ${convertToCountTypeString(
            this.expectedCount,
            this.countType
          )} required`;
        else if (this.selectedControlType === ControlType.maximum)
          result += ` approved. Up to ${convertToCountTypeString(
            this.expectedCount,
            this.countType
          )}`;
        else
          result += ` ${convertToCountTypeString(
            this.expectedCount,
            this.countType
          )}`;
      } else {
        result += " approved";
      }
      if (this.selectedCondition) {
        result += ` (${this.selectedCondition})`;
      }
      if (this.reason) {
        result += ` (${this.reason})`;
      }
    }
    return result;
  };
}

export interface IScheduleOptions {
  expiryCallback?: boolean;
  dailyReport?: boolean;
  morningReport?: boolean;
  forceSchedule?: boolean;
  state?: unknown;
  maxTime?: string;
  isCustomSchedule?: boolean;
  customAttendance?: {
    holdMinMinutes: number;
    holdMaxMinutes: number;
    responseTimeoutSec?: number;
    penaltyKeys?: string[];
    positionKeys?: string[];
  };
}

export interface ISchedulerTimerInfo extends IScheduledItem, IScheduledInfo {
  approvalId?: string; // if we have sent off and awaiting a response
  startTime?: Date;
  duration?: number;
  scheduleOptions?: IScheduleOptions;
  isPaused: true;
  isComplete: false;
  missEventPenalties?: string[];
  missEventPillories?: string[];
}

export interface ITaskTimerInfo extends ITimedBasketInfo, ITaskItem {
  /// specific timer interface for the timer stored in state for a credit
  isPenalty: boolean;
  selectedCondition?: string;
  expectedCount?: number;
  reason?: string;
  ownerAdded?: boolean;
  ownerName?: string;
  permanent?: boolean;
  endTime?: Date;
  endTriggerEventName?: string;
  isRoulette?: boolean;
  isPillory?: boolean;
  selectedItems?: Record<string, string>; // [key, itemName] [- condition if any]
}

export interface IPenaltyTimerInfo extends ITimedBasketInfo, IPenaltyItem {
  isPillory?: boolean;
  isPenalty: boolean;
  selectedCondition?: string;
  expectedCount?: number;
  reason?: string;
  ownerAdded?: boolean;
  ownerName?: string;
  endTime?: Date;
  endTriggerEventName?: string;
  isRoulette?: boolean;
  selectedItems?: Record<string, string>; // [key, itemName] [- condition if any]
}

export interface IRollPenalty {
  reason: string;
  points: number;
}

export interface RollState {
  didRoll: boolean;
  days?: number;
  audit: string[];
}

export interface IRollResults {
  seedCredits: ITaskBasketInfo;
  penalties: IRollPenalty[];
  bankCharge: number;
}

export interface ITaskBasketInfo {
  [creditItems: string]: ITaskTimerInfo | IPenaltyTimerInfo;
}

export interface IPermissionBasketInfo {
  [permissionItems: string]: IPermissionTimerInfo;
}

export interface IPositionBasketInfo extends IBasketInfo {
  duration?: number;
  endTime?: Date;
  startedTime?: Date;
  expectedCount: number;
  countType: CountType;
  hasPosition: boolean;
  hasMetronome?: boolean;
  hasSiteEffects?: boolean;
  taskKey?: string;
  audioUrl?: string;
  intervalMs?: number;
  siteEffectsSettings?: ISiteEffectsSettings;
  initialCount: number;
}

export interface IBasket {
  tasks: TaskBasketItem[];
  penalties: PenaltyBasketItem[];
  permissions: PermissionBasketItem[];
  positions: PositionBasketItem[];
  pendingItems: BaseBasketItem[];
  possiblePenalties?: BaseBasketItem[];
  chanceTasks?: (TaskBasketItem | PenaltyBasketItem)[];
  childTasks?: Record<string, (TaskBasketItem | PenaltyBasketItem)[]>;
}

export interface ILockState {
  lockType: LockTypes;
  unlockDate?: Date;
  count?: number;
  countType: LockCountType;
  lock?: boolean;
}

export interface IPilloryTopic extends IPendingItem {
  id?: string;
  name: string;
  reason: string;
  userid: string;
  username: string;
  ownername: string;
  countType: CountType;
  incrementCount: number;
  pilloryEndTime: string;
  isRoulette: boolean;
  doubleCountVotes?: boolean;
  currentTotal: number;
  votedMembers: { user: string; isUp: boolean; time: number }[];
  voteOptions: Record<string, number>;
}

export class PilloryTopic implements IPilloryTopic {
  constructor(topic: IPilloryTopic) {
    this.id =
      topic.id ??
      `${topic.userid}${topic.item?.key}${getTime(
        new Date(topic.pilloryEndTime)
      )}`;
    this.name = topic.name;
    this.reason = topic.reason;
    this.userid = topic.userid;
    this.username = topic.username;
    this.ownername = topic.ownername;
    this.countType = topic.countType;
    this.pilloryEndTime = topic.pilloryEndTime;
    this.incrementCount = topic.incrementCount;
    this.currentTotal = topic.currentTotal || topic.incrementCount;
    this.isRoulette = topic.isRoulette;
    this.updateType = topic.updateType;
    this.item = topic.item;
    this.date = topic.date;
    this.fireImmediate = topic.fireImmediate;
    this.customDecisionResponse = topic.customDecisionResponse;
    this.itemAdditionOptions = topic.itemAdditionOptions;
    this.votedMembers = topic.votedMembers;
    if (this.votedMembers) {
      this.votedMembers.forEach((member, idx) => {
        if (!member.user) {
          this.votedMembers[idx] = {
            user: member.toString(),
            isUp: true,
            time: Date.now(),
          };
        }
      });
    }
    this.doubleCountVotes = topic.doubleCountVotes;
    this.voteOptions = topic.voteOptions ?? {};
    const countItem = this.item as any as ICountSupport;
    if (countItem && countItem.count) {
      let minimumVote = round(countItem.count * 0.1);
      if (minimumVote < 1) minimumVote = isTimedItem(this.countType) ? 2 : 5;
      if (this.incrementCount < minimumVote) this.incrementCount = minimumVote;
    }
  }

  id: string;
  name: string;
  reason: string;
  userid: string;
  username: string;
  ownername: string;
  countType: CountType;
  pilloryEndTime: string;
  incrementCount: number;
  currentTotal: number;
  voteOptions: Record<string, number>;
  doubleCountVotes?: boolean;
  isRoulette: boolean;
  updateType: PendingUpdateType;
  item?: ISettingsItem;
  date?: string;
  fireImmediate?: boolean;
  itemAdditionOptions?: ItemAdditionOptions;
  customDecisionResponse?: IEventCustomDecisionResponse;
  votedMembers: { user: string; isUp: boolean; time: number }[];

  describe = () => `Penalty: ${this.name}
        Voting ${this.hasEnded()
      ? "has ended."
      : `expires ${formatValidDate(
        this.pilloryEndTime as unknown as Date,
        "dd MMM yyyy HH:mm"
      )}`
    }
        
        ${this.ownername} has given ${this.username} the penalty ${this.name}.
        ${this.reason ? `because: ${this.reason}` : ""} ${this.itemAdditionOptions?.selectedCondition
      ? `\n${this.ownername} has selected it will be using ${this.itemAdditionOptions.selectedCondition}`
      : ""
    }

        ${!this.hasEnded()
      ? `Click add to increase ${this.describeIncrement()}`
      : ""
    }
    `;

  describeIncrement = () =>
    convertToCountTypeString(this.incrementCount, this.countType);

  getCurrentCount = () => {
    if (this.doubleCountVotes) return this.currentTotal * 2;
    return this.currentTotal;
  };

  validateVote = (
    userid: string,
    isDownVote: boolean,
    cookiesService: Cookies,
    isOwner?: boolean
  ): { error: boolean; message?: string } | undefined => {
    if (userid === this.userid && isDownVote) {
      this.doubleCountVotes = true; // user is voting down on themselves, double the impact of their vote
      return { error: true, message: "Unable to vote on own pillory" };
    }
    if (!isOwner && !this.canVote(userid, cookiesService)) {
      return { error: true, message: "Already voted on this pillory" };
    }
    if (this.hasEnded()) {
      return { error: true, message: "Voting has finished on this pillory" };
    }
  };

  hasEnded = () => {
    const checkTime = new Date(this.pilloryEndTime);
    if (!checkTime || isNaN(checkTime.getTime())) return true;
    return isBefore(checkTime, new Date());
  };

  canVote = (userid: string, cookies: Cookies) => {
    if (this.hasEnded()) return false;
    if (userid === import.meta.env.VITE_GUEST_NAME) {
      // need to check in cookies if this user has already voted on this one...
      return !cookies.voteItems?.includes(this.id);
    }
    if (this.votedMembers && this.votedMembers.find((v) => v.user === userid))
      return false;
    return !this.hasEnded();
  };

  getVoteHistory = (): {
    user: string;
    value: number;
    time: Date;
    isUp: boolean;
  }[] => {
    const voteHistory: {
      user: string;
      value: number;
      time: Date;
      isUp: boolean;
    }[] = [];
    let currentIncrement = this.incrementCount;
    voteHistory.push({
      user: "initial",
      value: currentIncrement,
      time: this.date ? parseISO(this.date) : new Date(),
      isUp: true,
    });

    if (this.votedMembers) {
      this.votedMembers.forEach((m) => {
        const increment = m.isUp
          ? this.incrementCount
          : (-1 * this.incrementCount) / 2;
        currentIncrement =
          currentIncrement > this.incrementCount
            ? currentIncrement
            : this.incrementCount;
        if (!isNaN(increment)) currentIncrement += increment;
        if (currentIncrement < this.incrementCount)
          currentIncrement = this.incrementCount;
        voteHistory.push({
          ...m,
          value: currentIncrement,
          time: new Date(m.time),
          isUp: m.isUp,
        });
      });
    }
    return voteHistory;
  };

  voteUp = (
    userid: string,
    cookiesService: Cookies,
    option?: string
  ): { error: boolean; message?: string } | undefined => {
    const hasError = this.validateVote(userid, false, cookiesService);
    if (!hasError) {
      this.currentTotal += this.incrementCount;
      this.votedMembers.push({ user: userid, isUp: true, time: Date.now() });
      if (option) {
        if (!this.voteOptions[option]) this.voteOptions[option] = 1;
        else this.voteOptions[option] += 1;
      }
    }
    return hasError;
  };

  voteDown = (
    userid: string,
    cookiesService: Cookies,
    isOwner?: boolean
  ): { error: boolean; message?: string } | undefined => {
    const hasError = this.validateVote(userid, true, cookiesService, isOwner);
    if (!hasError) {
      this.currentTotal -= this.incrementCount / 2;
      if (this.currentTotal < this.incrementCount / 2)
        this.currentTotal = this.incrementCount / 2;
      this.votedMembers.push({ user: userid, isUp: false, time: Date.now() });
    }
    return hasError;
  };
}

export interface IAttendanceRequest {
  positionKey: string;
  penaltyKey: string;
  endTimeUtc: string;
  handled: boolean;
  fullScreen?: boolean;
  lastMessage?: string;
  holdDurationSec?: number;
  releaseTimeUtc?: string;
}

export interface IVoteTask extends ILovenseToy, IPictureItem, IKeyedItem {
  itemType: ItemTypes;
  description: string;
  name: string;
  userid: string;
  username: string;

  initialMinutes: number;
  durationMinutes: number;
  startDateTime: string | Date;
  votedMembers: Record<string, { user: string; isUp: boolean; time: number }[]>;
  previousDays?: number[];
  countType: CountType;
  voteCount: number;
}

export class VoteTask implements IVoteTask {
  today = formatValidDate(new Date(), "yyyy-MM-dd");
  constructor(task: IVoteTask) {
    this.id = task.userid + task.key;
    this.name = task.name;
    this.userid = task.userid;
    this.username = task.username;
    this.img = task.img;
    this.imgId = task.imgId;
    this.votedMembers = task.votedMembers;
    this.previousDays = [];
    this.startDateTime = new Date(task.startDateTime);
    const daysSince = differenceInDays(new Date(), this.startDateTime);
    if (task.previousDays && task.previousDays.length > 0)
      this.previousDays = task.previousDays.slice(0, daysSince); // only have previous days for days in past
    if (task.votedMembers) {
      Object.keys(task.votedMembers).forEach((date) => {
        if (this.previousDays.length >= daysSince) return;
        if (date === this.today || !task.votedMembers[date]) return;
        this.previousDays.push(
          task.votedMembers[date].reduce((p, c) => p + (c.isUp ? 1 : -1), 0)
        );
        delete this.votedMembers[date];
      });
    }
    this.durationMinutes = task.durationMinutes;
    this.initialMinutes = task.initialMinutes;
    this.key = task.key;
    this.itemType = task.itemType;
    this.description = task.description;
    this.lovenseProfile = task.lovenseProfile;
    this.voteCount = task.voteCount;
    this.lovenseToyId = task.lovenseToyId;
    this.countType = task.countType;
    const increment = this.getVoteIncrement();

    if (increment > 720) {
      this.voteCount = 720; // default it back to half a day per vote if the vote is silly over a day.
    } else if (increment < 5) {
      this.voteCount = 5; // lets make sure there is no silly 1 min additions any more, make it something more standard
    }
  }

  key: string;
  itemType: ItemTypes;
  description: string;
  id: string;
  name: string;
  userid: string;
  username: string;
  imgId: string | undefined;
  img?: string;
  startDateTime: Date;
  initialMinutes: number;
  lovenseProfile?: LovensePresetName;
  voteCount: number;
  countType: CountType;
  lovenseToyId?: string;
  durationMinutes: number;
  votedMembers: Record<
    string,
    {
      user: string;
      isUp: boolean;
      time: number;
      isVoteTask?: boolean;
      voteTaskAmount?: number;
    }[]
  >;
  previousDays: number[];

  describe = () => `Vote on ${this.name}

        Voting ${this.hasEnded()
      ? "has ended."
      : `expires ${formatValidDate(
        this.getEndTime(),
        "dd MMM yyyy HH:mm"
      )}`
    }             

        ${!this.lovenseToyId && !this.hasEnded()
      ? `Click add to increase ${convertToCountTypeString(
        this.getVoteIncrement(),
        CountType.minutes
      )}
        Click reduce to remove ${convertToCountTypeString(
        this.getVoteIncrement() / 2,
        CountType.minutes
      )}`
      : ""
    }`;

  getEndTime = () => {
    return addMinutes(this.startDateTime, this.durationMinutes);
  };

  getVoteIncrement = () =>
    this.voteCount
      ? this.voteCount
      : this.initialMinutes * 0.1 > 720
        ? 720
        : this.initialMinutes * 0.1;

  getVoter = (userid: string) => {
    const s: Record<string, number> = {};
    const date = formatValidDate(new Date(), "yyyy-MM-dd");
    if (this.votedMembers[date]) {
      this.votedMembers[date].reduce((c, p) => {
        if (!c) c = {};
        if (p.user) {
          let count = 0;
          if (c[p.user]) count = c[p.user];
          c[p.user] = count += 1;
        }
        return c;
      }, s);
    }
    return s[userid];
  };

  getVoteHistory = (): {
    user: string;
    value: number;
    time: Date;
    isUp: boolean;
  }[] => {
    let voteHistory: {
      user: string;
      value: number;
      time: Date;
      isUp: boolean;
    }[] = [];
    let currentDurationMinutes = this.initialMinutes;
    voteHistory.push({
      user: "initial",
      value: currentDurationMinutes,
      time: this.startDateTime,
      isUp: true,
    });

    this.previousDays.forEach((dayTotal, idx) => {
      currentDurationMinutes += dayTotal * this.getVoteIncrement();
      currentDurationMinutes -= 24 * 60; // take a day off for each day in history
      if (currentDurationMinutes < 0) {
        currentDurationMinutes = 0;
        return voteHistory;
      }
      voteHistory.push({
        value: currentDurationMinutes,
        user: "Archived Day",
        isUp: true,
        time: addDays(new Date(), (this.previousDays.length - idx) * -1),
      });
    });

    if (voteHistory.length > 30) {
      voteHistory = voteHistory.slice(voteHistory.length - 31);
    }

    if (currentDurationMinutes > 0 && this.startDateTime < startOfToday()) {
      currentDurationMinutes -=
        new Date().getHours() * 60 + new Date().getMinutes(); // take off time spent today
    }

    if (currentDurationMinutes < 0) {
      currentDurationMinutes = 0;
      return voteHistory;
    }

    if (this.votedMembers[this.today]) {
      this.votedMembers[this.today].forEach((m) => {
        const defaultIncrement =
          m.isVoteTask && m.voteTaskAmount
            ? m.voteTaskAmount
            : this.getVoteIncrement();
        const increment = m.isUp
          ? defaultIncrement
          : (-1 * defaultIncrement) / 2;
        if (!isNaN(increment)) currentDurationMinutes += increment;
        voteHistory.push({
          ...m,
          value: currentDurationMinutes > 0 ? currentDurationMinutes : 0,
          time: new Date(m.time),
          isUp: m.isUp,
        });

        if (currentDurationMinutes < 0) {
          currentDurationMinutes = 0;
          return voteHistory;
        }
      });
    }
    return voteHistory;
  };

  validateVote = (
    userid: string,
    cookies: Cookies,
    isOwner?: boolean
  ): { error: boolean; message?: string } | undefined => {
    if (!isOwner && !this.canVote(userid, cookies)) {
      return { error: true, message: "Already voted on this task today" };
    }

    if (this.hasEnded()) {
      return { error: true, message: "Voting has finished on this task" };
    }
  };

  hasEnded = () => {
    if (
      isNaN(this.durationMinutes) ||
      !this.startDateTime ||
      isNaN(this.startDateTime.getTime())
    )
      return true;
    return isBefore(this.getEndTime(), new Date());
  };

  canVote = (userid: string, cookies: Cookies) => {
    if (this.hasEnded()) return false;
    if (userid === import.meta.env.VITE_GUEST_NAME) {
      // need to check in cookies if this user has already voted on this one...
      return !cookies.voteItems?.includes(this.key);
    }
    const dayRecord = this.votedMembers[this.today] || [];
    if (this.userid === userid || dayRecord.find((rec) => rec.user === userid))
      return false;
    return true;
  };
  canViewVote = (userid: string) => {
    if (this.hasEnded()) return false;
    const dayRecord = this.votedMembers[this.today] || [];
    if (dayRecord.find((rec) => rec.user === userid)) return false;
    return true;
  };

  voteUp = (
    userid: string,
    cookies: Cookies
  ): { error: boolean; message?: string } | undefined => {
    const hasError = this.validateVote(userid, cookies);
    if (!hasError) {
      this.durationMinutes += this.getVoteIncrement();
      const dayRecord = this.votedMembers[this.today] || [];
      dayRecord.push({ user: userid, isUp: true, time: new Date().getTime() });
      this.votedMembers[this.today] = dayRecord;
    }
    return hasError;
  };

  voteTaskUpdate = (config: ITaskVoteConfig) => {
    const minutes = getCountMinutes(config.voteIncrement, config.countType);
    if (config.isUp) this.durationMinutes += minutes;
    else this.durationMinutes -= minutes;
    const dayRecord = this.votedMembers[this.today] || [];
    dayRecord.push({
      user: "System Update",
      isUp: config.isUp,
      time: new Date().getTime(),
      isVoteTask: true,
      voteTaskAmount: minutes,
    });
    this.votedMembers[this.today] = dayRecord;
  };

  nudge = (userid: string): void => {
    const dayRecord = this.votedMembers[this.today] || [];
    dayRecord.push({ user: userid, isUp: true, time: new Date().getTime() });
    this.votedMembers[this.today] = dayRecord;
  };

  voteDown = (
    userid: string,
    cookies: Cookies,
    isOwner?: boolean
  ): { error: boolean; message?: string } | undefined => {
    const hasError = this.validateVote(userid, cookies, isOwner);
    if (!hasError) {
      this.durationMinutes -= this.getVoteIncrement() / 2;
      if (this.durationMinutes < 0) this.durationMinutes = 0;
      const dayRecord = this.votedMembers[this.today] || [];
      dayRecord.push({ user: userid, isUp: false, time: new Date().getTime() });
      this.votedMembers[this.today] = dayRecord;
    }
    return hasError;
  };
}

export interface IPendingItem {
  updateType: PendingUpdateType;
  item?: ISettingsItem;
  date?: string;
  fireImmediate?: boolean;
  itemAdditionOptions?: ItemAdditionOptions;
  customDecisionResponse?: IEventCustomDecisionResponse;
  customScheduleKey?: string;
  gameRequest?: IScheduledGame;
}

export interface IEventCustomDecisionResponse {
  itemKey: string;
  decision: boolean;
  expectedCount?: number;
  countType?: CountType;
  addCountLimits?: boolean;
  customCondition?: boolean;
  selectedCondition?: string;
  reason?: string;
  selectedControlType?: ControlType;
}

export interface ItemAdditionOptions extends ILovenseToy {
  selectedCondition?: string;
  ownerAdded?: boolean;
  ownerName?: string;
  countType?: CountType;
  expectedCount?: number;
  reason?: string;
  eventEndTrigger?: string; // allows tasks to be overriden to run until an event
  decision?: boolean;
  isPillory?: boolean;
  isVote?: boolean;
  isRoulette?: boolean;
  selectedItemGroupKeys?: string[];
  selectedItemGroupOptions?: Record<string, string>;
  isPeek?: boolean;
  selectedChoiceName?: string;
  isChoiceName?: boolean;
  requestedByKey?: string;
  requestedByName?: string;
  customScheduleKey?: string;
  autoStartOnRequestedByStart?: boolean;
}

export interface AdditionDialogResponse extends ILovenseToy {
  date: Date;
  expectedCount?: number;
  countType: CountType;
  item: ISettingsItem;
  addCountLimits: boolean;
  customCondition: boolean;
  selectedCondition?: string;
  selectedChoiceName?: string;
  selectedItems?: Record<string, string>;
  selectedItemsGroupKeys?: string[];
  reason: string;
}

export interface IEditDialogState<T extends ISettingsItem>
  extends IEditDialogErrorState {
  open?: boolean;
  editItem: T;
}

interface IEditDialogErrorState {
  missingInfo?: boolean;
  countNotSet?: boolean;
  weeklyTargetValueNotSet?: boolean;
  weeklyTargetUnitNotSet?: boolean;
  optionToAdd?: string;
  optionNotAdded?: boolean;
  triggerToAdd?: string;
  triggerNotAdded?: boolean;
  rouletteEmpty?: boolean;
  invalidPilloryItems?: boolean;
  countTypeNotSet?: boolean;
  timeNotSet?: boolean;
  itemsUsedInUpdate?: boolean;
  incompleteGroups?: boolean;
  editVoteTaskInProgress?: boolean;
  incompleteVoteTask?: boolean;
}

export interface IActionConfirmState {
  open: boolean;
  itemKey?: string;
  item?: any;
  message?: string;
  callback?: (isOK: boolean) => void;
}

export interface IItemAdditionDialogState {
  open: boolean;
  item?: ISettingsItem;
  // expectedCount?: number;
  // countType: CountType;
  // options?: string[];
  userid?: string;
  username?: string;
  settings: StatefulSettings;
  type?: ItemTypes;
  day?: EventDay;
  itemAdditionOptions?: ItemAdditionOptions;
  onAdd?: (ok: boolean, updatedItem?: ISettingsItem) => void;
}

export interface IShareItemState {
  open: boolean;
  messageRecipients: ControlRecord[];
  item?: ISettingsItem;
  cb?: (isOK: boolean, user?: ControlRecord) => void;
}

export interface EncryptedOwnedItemsCollection {
  [itemKey: string]: string;
}
export interface OwnedItemsCollection {
  [itemKey: string]: string[];
}


export interface ISystemConfig {
  timeouts: {
    DATABASE_CONNECT_DELAY_MS: number;
    DATABASE_RECONNECT_MS: number;
    DATABASE_UPDATE_MS: number;
    DATABASE_TRANSACTION_TIMEOUT: number;
  };
  allowLocks: boolean;
  featureFlags: Record<string, Record<string, boolean>>;
  hotTopics: Record<string, number>;
  help: Record<string, IHelpTopic>;
  bannedIps: { ip: string; reason: string }[];
  ignoreDiscordBotCategories: string[];
  system?: {
    memberAvailableDays?: string;
    memberOwnedItems?: string;
    memberEditProfile?: string;
    memberEditSettings?: string;
    welcomeOwner?: string;
    welcomeMember?: string;
    ownerEditProfile?: string;
  };
  ownerProfileTypes: string[];
  prohibitedSyntax: string[];
  profanities: string[];
  perks: IReward[];
  dashboardLayout: any;
  siteFxCuratedSources?: ISiteFxCuratedSourcesConfig;
}

export interface IScheduledInfo {
  scheduleReason: string;
  unavailable?: boolean;
  randomPlaceholder?: boolean;
  childTask?: boolean;
  randomSelectedKey?: string;
  fromKey?: string;
  possiblePenaltyItem?: boolean;
  chanceItem?: boolean;
  requestedByKey?: string;
  requestedByName?: string;
  customScheduleKey?: string;
  autoStartOnRequestedByStart?: boolean;
  autoStartedByRequestedBy?: boolean;
}

export interface IDaySchedule {
  estHours: number;
  day: EventDay;
  busy: boolean;
  unavailable: boolean;
  weekend: boolean;
  tasks: ((ITaskItem | IPenaltyItem) & IScheduledInfo)[];
  perms: (IPermissionItem & IScheduledInfo)[];
  events: (IEventItem & IScheduledInfo)[];
}

export const isRandomKey = (key: string) => key && key.startsWith("random");

export const randomTask = (id: string) =>
({
  key: "randomTask" + id,
  name: "Random Task Selected",
  Description: "A random task will be selected",
  randomPlaceholder: true,
  itemType: ItemTypes.task,
} as any as ITaskItem & IScheduledInfo);

interface IChartDataset {
  label: string;
  data: number[];
  backgroundColor?: any;
  borderColor?: string;
  borderWidth?: number;
  yAxisID?: string;
}

export interface IChartJSDatasetShape {
  labels: string[];
  tooltips?: string[];
  datasets: IChartDataset[];
}

export interface Task extends ICountSupport {
  name?: string;
  description?: string;
  image?: string;
  availabilityDayType: AvailabilityDayType;
  count?: number;
  countType: CountType;
  items: ITaskItem[];
  categories?: string[];
}

export interface Item extends ICountSupport {
  name?: string;
  description?: string;
  image?: string;
  availabilityDayType: AvailabilityDayType;
  count?: number;
  countType: CountType;
  categories?: string[];
}

export interface IShowNotificationState {
  message: string;
  persist?: boolean;
  error?: boolean;
  fatal?: boolean;
  social?: boolean;
  success?: boolean;
  warning?: boolean;
  icon?: string;
  onClick?: () => void;
  suppressSound?: boolean;
  notificationType: NotificationType;
}

export interface MessageDialogState {
  message?: string;
  title?: string;
  subject?: string;
  username?: string;
  readonly?: boolean;
  userid?: string;
  isReply?: boolean;
  toUsername?: string;
  toUserid?: string;
  fromUsername?: string;
  fromUserid?: string;
  row?: IMemberMessage;
  disableReply?: boolean;
  state?: any;
  messageRecipientNames?: string[];
  messageType: MessageType;

  canAcceptSessionOffer?: boolean;
  canOfferSession?: boolean;
  canRedeem?: boolean;
  canApprovePermission?: boolean;
  canApprovePostponeRequest?: boolean;
  canApproveRequest?: boolean;
  canApproveWeeklyTarget?: boolean;
  canApplyWeeklyTargetPenalty?: boolean;
  canViewRequest?: boolean;
  canRedoLines?: boolean;
  canAddTodo?: boolean;

  permissionDecision?: (decision: boolean, state: MessageDialogState) => void;
  redoLines?: (state: MessageDialogState) => void;
  approveRequest?: (state: MessageDialogState) => void;
  weeklyTargetDecision?: (
    decision: "approved" | "applyPenalty" | "ended",
    state: MessageDialogState,
    nextWeekTargetValue?: number
  ) => void;
  postponeItem?: (state: MessageDialogState) => void;
  viewRequest?: (state: MessageDialogState) => void;
  replyMessage?: (row: IMemberMessage) => MessageDialogState;
  offerSession?: (state: MessageDialogState) => void;
  acceptSessionOffer?: (state: MessageDialogState) => void;
  redeemReward?: (state: MessageDialogState) => void;
  sendUserMessage?: (
    message: string,
    toUsername: string,
    subject: string,
    toUserid?: string
  ) => void;
  addTodo?: (state: MessageDialogState) => void;
}

export class ShowProfileProps {
  constructor(profile: ControlRecord, editorSettings: StatefulSettings) {
    this.profile = editorSettings.getMemberCommunityProfile(profile);
    this.ownerProfile = editorSettings.getMemberOwnerProfileSummary();
    this.ownedSessions = editorSettings
      .getAllControlRecords()
      .filter((m) => m.ownerid === this.profile?.userid);
    this.isTopMember = editorSettings
      .getTopMembers()
      .includes(this.profile?.userid);
    this.isTopOwner = editorSettings
      .getTopOwners()
      .includes(this.profile?.userid);
    this.isBottomMember = editorSettings
      .getBottomMembers()
      .includes(this.profile?.userid);
  }

  profile?: ControlRecord;
  ownerProfile?: IOwnerProfileSession;
  ownedSessions?: ControlRecord[];
  isTopMember?: boolean;
  isBottomMember?: boolean;
  isTopOwner?: boolean;
}

export interface ILockReceipt {
  startTime: string;
  userid: string;
  taskKey: string;
  taskName: string;
  taskImg: string;
  imageKey: string;
  messageId: string;
  username: string;
  ownerid: string;
  ownername: string;
  isPaused?: boolean;
  isComplete?: boolean;
  /** Set to true once the unlock image has been successfully sent to the member.
   * Used as an idempotency marker in the cloud function to prevent duplicate DMs on retry. */
  imageSent?: boolean;
  /** HTTPS download URL from Firebase Storage (new receipts) */
  imageUrl?: string;
  /** Legacy: base64 data URL stored directly in receipt (old receipts) */
  image?: string;
  /** Legacy: base64 string stored in receipt (old receipts) */
  imageString?: string;
}

export interface ILockPayload extends IKeyedItem {
  method: string;
  taskKey: string;
  taskName: string;
  taskImg: string;
  userid: string;
  username: string;
  ownerid: string;
  ownername: string;
  /** HTTPS download URL from Firebase Storage (replaces base64 image field) */
  imageUrl: string;
  imageName: string;
}

export interface IProofPayload extends IKeyedItem {
  method: string;
  taskKey: string;
  taskName: string;
  taskImg: string;
  userid: string;
  username: string;
  ownerid: string;
  ownername: string;
  image: string;
  imageName: string;
  achievedValue?: number;
}

export class LovenseCommand {
  /**
   *
   * @param devToken the key of the developer api
   * @param toyId the toy to control
   * @param totalSeconds the total time for the command
   * @param strengthPattern example 20;20;5;20;10
   * @param loopRunningSec if set this defines the loop
   * @param loopPauseSec if set along with loopRunningSec this defines the loop
   * @param rule V:1;F:v;S:1000# i.e. F:vrp; Features: v is vibrate, r is rotate, p is pump,this should match the strength below; S:1000; Intervals in Milliseconds, should be greater than 100.
   * @param name preset name can be pulse, wave, fireworks, earthquake
   * @param command type of command, can be function, pattern or preset
   * @param actionType Actions can be Vibrate, Rotate, Pump or Stop. Use Stop to stop the toy’s response.
   */
  constructor(
    devToken: string,
    userid: string,
    toyId: string,
    totalSeconds?: number,
    command?: "Function" | "Pattern" | "Preset",
    name?: LovensePresetName,
    loopRunningSec?: number,
    loopPauseSec?: number,
    actionType?: "Vibrate" | "Rotate" | "Pump" | "Stop",
    strengthPattern?: string,
    rule?: string
  ) {
    this.token = devToken;
    this.uid = userid;
    this.toy = toyId;
    this.timeSec = totalSeconds ?? 60; // default to 60 if not known
    this.strengthPattern = strengthPattern;
    this.loopRunningSec = loopRunningSec;
    this.loopPauseSec = loopPauseSec;
    this.rule = rule;
    this.command = command;
    this.name = name ?? LovensePresetName.pulse;
    this.actionType = actionType;
  }
  actionType?: "Vibrate" | "Rotate" | "Pump" | "Stop";
  strengthPattern?: string;
  token: string;
  rule?: string;
  uid: string;
  toy: string;
  name: LovensePresetName;
  command?: "Function" | "Pattern" | "Preset";
  get action() {
    return `${this.actionType}:${this.strengthPattern?.toString()}`;
  }
  timeSec: number;
  loopRunningSec?: number;
  loopPauseSec?: number;
  apiVer = 1;

  static getToyName = (toy: LovenseToy | undefined) => {
    if (!toy) return "Not Found!";
    if (toy.nickName?.length > 0) return toy.nickName;
    if (toy.name?.length > 0) return toy.name;
    return "";
  };
}

export interface LovenseToy {
  nickName: string;
  name: string;
  id: string;
  status: number;
}

export interface Holiday {
  from: Date;
  to: Date;
}

export interface ICustomSchedule {
  events?: ICustomScheduleEvent[];
  name: string;
  key: string;
  userid?: string;
  username?: string;
  published?: boolean;
  scheduledDate?: string;
  tags?: string[];
  eventCount?: number;
  summaryMinTime?: string;
  summaryMaxTime?: string;
  positionsEnabled?: boolean;
  positionCount?: number;
  metronomeEnabled?: boolean;
  metronomeCount?: number;
  metronomeTaskKey?: string;
  metronomeAudioUrl?: string;
  metronomeImg?: string;
  metronomeImgId?: string;
  siteEffectsEnabled?: boolean;
  siteEffectsCount?: number;
  siteEffectsDurationSec?: number;
  siteEffectsImg?: string;
  siteEffectsImgId?: string;
  siteEffectsSettings?: ISiteEffectsSettings;
  dailyReporting?: boolean;
  minReportTime?: string;
  maxReportTime?: string;
  reportStyle?: ReportStyle;
  reportWordCount?: number;
  attendanceEnabled?: boolean;
  attendanceCount?: number;
  attendanceMinMinutes?: number;
  attendanceMaxMinutes?: number;
  attendanceResponseTimeoutSec?: number;
  attendancePenaltyKeys?: string[];
  attendancePositionKeys?: string[];
}

export interface ICustomScheduleEvent extends IEventItem {
  title?: string;
  itemOptions: Record<string, string>;
  isChoiceName?: boolean;
  runTasksToNextEvent?: boolean;
  taskDurationMinutes?: number;
  awaitTaskComplete?: boolean;
  gameKeys?: string[];
  gameResultPenalties?: string[];
  selectedGameCountPerError?: number;
};

export interface IShoppingRequest extends IKeyedItem {
  item: ITaskItem;
  url: string;
  description: string;
  requestedBy?: IUserRecord;
  review?: boolean;
}

export interface ITodoItem extends IPictureItem, INamedItem, IKeyedItem {
  description: string;
  targetPage?: string;
  targetTab?: string;
  targetDialog?: string;
  targetDialogArgs?: any;
}

export interface IWizardTemplate extends IKeyedItem {
  name: string;
  userid?: string;
  startTime: string;
  endTime: string;
  createExtraEvents: boolean;
  extraEventCount: number;
  addTaskKeys: boolean;
  addGames: boolean;
  enableProofRequests: boolean;
  enableMissEventPenalties: boolean;
  excludeOutfits: boolean;
  excludeClothes: boolean;
  outfitKey?: string;
  extraInfo?: string;
}

export interface ITypingStats {
  keystrokes: number;
  starttime: Date;
  deletekeys: number;
  words: number;
  wordsperminute: number;
  timespent: number;
}

export interface ICachedRandomSelectionProvider {
  getPreviousItemSelectionState: () => Record<string, string[]>;
  updatePreviousItemSelections: (
    key: string,
    vals: any[],
    add: boolean
  ) => void;
}

export interface TourStep {
  selector: string;
  content: string;
}

export interface ConfirmState {
  open: boolean;
  title?: string;
  message?: string;
  callback?: (isOK: boolean) => void;
}

export const defaultSodEvent = new EventItem({
  description: "",
  name: "Start Of Day",
  img: "",
  imgId: "",
  approvalChance: 1,
  optionAppliedChance: 1,
  availability: AvailabilityType.daily,
  minTime: "06:00",
  maxTime: "06:00",
  hasOptions: false,
  hidden: true,
  key: v1(),
  itemType: ItemTypes.event,
  disableAdd: true,
  eventType: EventType.startOfDay,
  availabilityDayType: AvailabilityDayType.AllDays,
});


export interface DebouncedFunc<T extends (...args: any[]) => any> {
  /**
   * Call the original function, but applying the debounce rules.
   *
   * If the debounced function can be run immediately, this calls it and returns its return
   * value.
   *
   * Otherwise, it returns the return value of the last invocation, or undefined if the debounced
   * function was not invoked yet.
   */
  (...args: Parameters<T>): ReturnType<T> | undefined;

  /**
   * Throw away any pending invocation of the debounced function.
   */
  cancel(): void;

  /**
   * If there is a pending invocation of the debounced function, invoke it immediately and return
   * its return value.
   *
   * Otherwise, return the value from the last invocation, or undefined if the debounced function
   * was never invoked.
   */
  flush(): ReturnType<T> | undefined;
}

export const getGameTypeName = (gameType: GameTypes) => {
  switch (gameType) {
    case GameTypes.quiz:
      return "Quiz";
    case GameTypes.wordsearch:
      return "Word Search";
    case GameTypes.higherlower:
      return "Higher or Lower";
    case GameTypes.minefield:
      return "Minesweeper";
    case GameTypes.memoryNumbers:
      return "Memory Numbers";
    case GameTypes.patternRecall:
      return "Pattern Recall";
    case GameTypes.cardFlip:
      return "Pairs";
    case GameTypes.battleShips:
      return "Battleships";
    case GameTypes.ticTacToe:
      return "Tic Tac Toe";
    case GameTypes.blackJack:
      return "Blackjack";
    case GameTypes.rockPaperScissors:
      return "Rock Paper Scissors";
    case GameTypes.snakesAndLadders:
      return "Snakes and Ladders";
    default:
      return gameType;
  }
};

export enum GameTypes {
  quiz = "quiz",
  wordsearch = "wordsearch",
  higherlower = "higherlower",
  minefield = "minefield",
  memoryNumbers = "memoryNumbers",
  patternRecall = "patternRecall",
  cardFlip = "cardFlip",
  battleShips = "battleShips",
  ticTacToe = "ticTacToe",
  blackJack = "blackJack",
  rockPaperScissors = "rockPaperScissors",
  fruitMachine = "fruitMachine",
  roulette = "roulette",
  snakesAndLadders = "snakesAndLadders",
}

export interface ISnakesAndLaddersGame extends IGame {
  squaresPerRow: number;
  includeMetronome?: boolean;
  /** Task key to use for metronome squares. */
  metronomeTaskKey?: string;
  /** Audio URL to use for metronome squares. */
  metronomeAudioUrl?: string;
  includeHypnosis?: boolean;
  /** Duration of each hypnosis / Site FX session in seconds (default: random 20-45). */
  hypnosisDurationSec?: number;
  /** Site FX content config to use for hypnosis squares (stored as plain object, cast on use). */
  hypnosisSettings?: object;
  taskKeys: string[];
  taskRepeatCount: number;
}

export interface IFruitMachineGame extends IGame {
  initialScore: number;
  requiredSpins: number;
}

export interface IRouletteGame extends IGame {
  initialScore: number;
  requiredSpins: number;
}

export interface IRockPaperScissorsGame extends IGame {
  rounds: number;
  minPassRate: number
}

export interface IBattleShipsGame extends IGame {
  gridSize: number;
  numberOfShips: number;
  maxAttempts: number;
  minNumberOfHits: number;
}

export interface ITicTacToeGame extends IGame {
  rounds: number;
  minPassRate: number;
}

export interface IBlackJackGame extends IGame {
  rounds: number;
  minPassRate: number;
}

export interface IMemoryNumbersGame extends IGame {
  sequenceLength: number;
  maxSequenceLength: number;
}

export interface IPatternRecallGame extends IGame {
  patternLength: number;
  maxPatternLength: number;
  gridSize: number;
}

export interface ICardFlipGame extends IGame {
  pairs: number;
  gridSize: number;
  flippedCards: string[]; // array of "row,col" strings
  matchedPairs: number;
}

export interface IGame extends IKeyedItem, INamedItem {
  description: string;
  startTime: Date;
  endTime?: Date;
  completed: boolean;
  userid: string;
  username: string;
  img?: string;
  imgId?: string;
  gameType: GameTypes;
  allowedMistakes?: number;
  allowedTimeMinutes?: number;
}

interface IQuizGameQuestion {
  question: string;
  options: string[];
  answer: string;
}

/// Quiz game where user has to answer multiple choice questions within a time limit and with a limited number of mistakes allowed
export interface IQuizGame extends IGame {
  questions: IQuizGameQuestion[];
  randomiseQuestions?: boolean;
  limitQuestions?: number;
  freeTextAnswers?: boolean;
}

/// Word Search game where user has to find words in a grid of letters within a time limit
export interface IWordSearchGame extends IGame {
  grid: string[][];
  wordList: string[];
  foundWords: string[];
  rows: number;
  cols: number;
}

/// Higher or Lower game where user has to guess if next number is higher or lower than current number must get a certain number correct to win
export interface IHigherOrLowerGame extends IGame {
  rounds: number;
  currentRound: number;
  minimumPassRate: number;
}

/// Minefield game where user has to pick cells without hitting a mine must pick a certain number of safe cells to win
export interface IMinefieldGame extends IGame {
  rows: number;
  cols: number;
  mines: number;
  revealedCells: string[]; // array of "row,col" strings
  attemptsRequired: number;
}

export interface IGameResult {
  gameKey: string;
  penaltyCount: number;
  completed: number;
  total: number;
  totalTime: number;
  mistakes: number;
  userid: string;
  username: string;
  date: Date;
}

export interface IScheduledGame { gameKey: string, penaltyKeys: string[], count: number }

/** Stores a member's self-reported ratings for an outfit option or custom schedule. */
export interface ITaskRating {
  difficulty?: number;  // 1–5 (😊→🥵)
  fun?: number;         // 1–5 (😞→🤩)
  frequency?: number;   // 1–5 (less often→more often)
  note?: string;        // optional free-text feedback
}
